// DataSurfaceV3 — primary working surface in V3.
//
// Vision (10/10 UX):
//  · Tabs across the top — every saved view is one click away
//  · Schema sidebar — auto-derived payload fields with frequency, click
//    to add as a projected column or as a filter row
//  · Live data table — newest-first, auto-refresh every 10s, pulse on
//    new arrivals, click cell → filter chip
//  · Inline recipe — filter rows + unique-by — applied live
//  · Save panel — name + ingest action (validate / dedupe / forward /
//    enrich) creates a BQ view AND optional trigger in one operation
//
// Same recipe SQL helpers as V2 (recipe-sql.ts). Same /bq-views,
// /views, /triggers backends — V3 is a new surface, not a new model.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Trigger, TriggerAction, Source, SourceInput } from '@datahub/shared';
import {
  createBqView,
  createTrigger,
  createView,
  deleteBqView,
  getEventsSchemaFlat,
  listSchemaDefs,
  previewBqView,
  reprocessEvent,
  type BqView,
  type SchemaDefinition,
  type SchemaField,
} from '../../api';
import { useToast } from '../Toast';
import { LineageDrawer } from '../lineage/LineageDrawer';
import { buildAutoProjectedSql, buildRecipeSql, filterRowsToTree, type FilterRow } from '../v2/recipe-sql';
import { BRAND_BY_ID, buildBrandFilterPredicate, buildBrandSqlCase } from './brand-rules';
import { useFocusRestore } from './use-focus-restore';
import { histogramCache } from './histogram-cache';
import { readPrefs, writePrefs, type DataSurfacePrefs } from './data-surface-prefs';
import { ViewTab } from './view-tab';
import { SchemaSidebar } from './schema-sidebar';
import { ShortcutsCheatSheet } from './shortcuts-cheat-sheet';
import { ColumnHeaderMenu } from './column-header-menu';
import { CellHoverPreview } from './cell-hover-preview';
import { DistributionDrawer } from './distribution-drawer';
import { DrillbackChip } from './drillback-chip';
import { FilterRailCompact } from './filter-rail-compact';
import { VolumeHistogram } from './volume-histogram';
import { ConnectedSheetToolbar, type ToolbarAction } from './connected-sheet-toolbar';
import { ConnectedSheetHeader } from './connected-sheet-header';
import {
  ENVELOPE_COLUMNS, NON_FILTERABLE_CELLS, HASH_COLUMNS, TIMESTAMP_COLUMNS,
  OP_SYMBOL, SYSTEM_FIELDS, ENVELOPE_FIELDS,
  fieldOrigin, fieldExpr, filterRowToPredicate, extractColumnNamesFromSql,
  fmtRelative, formatCell, isCellTruncated,
  renderHighlight, renderInputTags, hasEventIdColumn,
  formatSampleValue, matchFieldFilter,
  exportPreview, copyPreviewAsMarkdown,
  computeRowFingerprint, resolveFingerprintLabel,
  inputStyle, tabMenuItemStyle,
  fieldsThStyle, fieldTypeChipStyle, fieldOriginChipStyle,
  type FieldOrigin,
} from './data-surface-utils';
import { NestedCell } from '../../lib/nested-cell';

/** Cheap "looks like JSON object/array" probe. Avoids JSON.parse on every
 *  string cell. Returns the parsed value when it's a non-null
 *  object/array, undefined otherwise. */
function tryParseJsonObject(v: unknown): unknown | undefined {
  if (v === null || v === undefined) return undefined;
  if (typeof v === 'object') return v;
  if (typeof v !== 'string') return undefined;
  const s = v.trim();
  if (s.length < 2) return undefined;
  if (s[0] !== '{' && s[0] !== '[') return undefined;
  try {
    const parsed = JSON.parse(s);
    if (parsed === null) return undefined;
    if (typeof parsed === 'object') return parsed;
  } catch { /* not JSON, fall through */ }
  return undefined;
}

// histogramCache + inputStyle imported from local modules above.

type StreamAction = 'none' | 'validate' | 'dedupe' | 'forward' | 'enrich';

// Default interval. Operator can switch via the Connected-Sheet
// header "Refresh options" dropdown (5s / 30s / 1m / 5m / Manual).
// Persisted per-source in localStorage.
//
// Cost audit #2: previously 10_000 — but 10s isn't even in the
// picker (5s/30s/1m/5m/manual), every tick re-runs the recipe SQL
// against the T2 external table, and N open tabs compound. 1m is
// a sensible "polling is on, but not aggressive" default; operator
// who wants 5s flips the picker.
const DEFAULT_AUTO_REFRESH_MS = 60_000;
const REFRESH_PREF_PREFIX = 'v3.refresh.ms.';

export function DataSurfaceV3({ sourceId, source, dataset, views, triggers, onViewSaved }: {
  sourceId: string;
  source?: Source;
  dataset: string;
  views: BqView[];
  triggers: Trigger[];
  onViewSaved: () => void;
}): React.ReactElement {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Connected-Sheet full-screen mode — controlled via `?fs=1` URL
  // param so it's bookmarkable / sharable. Outer wrapper switches to
  // fixed-position viewport-fill when true; ESC exits. The TopHeader
  // breadcrumb stays above (its own z-index) so the operator never
  // loses the way home.
  const fullscreen = searchParams.get('fs') === '1';
  const setFullscreen = (next: boolean): void => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (next) p.set('fs', '1'); else p.delete('fs');
      return p;
    });
  };

  // Lock body scroll when fullscreen so the rest of SourceDetailV3Page
  // (hidden beneath the overlay) can't scroll out from under the data
  // table. Restored on exit.
  useEffect(() => {
    if (!fullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [fullscreen]);

  // Auto-refresh interval — persisted per-source in localStorage so the
  // operator's preference (every 5s for live debugging vs Manual for a
  // quiet exploration session) survives a reload. 0 = manual only.
  const refreshKey_PREFIX = `${REFRESH_PREF_PREFIX}${sourceId}`;
  const [refreshIntervalMs, setRefreshIntervalMs] = useState<number>(() => {
    try {
      const v = localStorage.getItem(refreshKey_PREFIX);
      const n = v === null ? DEFAULT_AUTO_REFRESH_MS : Number(v);
      return Number.isFinite(n) && n >= 0 ? n : DEFAULT_AUTO_REFRESH_MS;
    } catch { return DEFAULT_AUTO_REFRESH_MS; }
  });
  const persistRefreshInterval = (ms: number): void => {
    setRefreshIntervalMs(ms);
    try { localStorage.setItem(refreshKey_PREFIX, String(ms)); } catch { /* private mode */ }
  };

  // Lineage drawer state — populated when operator clicks an event_id
  // cell. Drawer shows parent chain + children + applied triggers.
  const [lineageEventId, setLineageEventId] = useState<string | null>(null);

  // Pinned column paths (B3 drill-in for V3). Operator clicks "+col"
  // inside a NestedCell expand panel to promote a nested path (e.g.
  // current.title, meta.action) to a top-level column on this table.
  // Persisted in localStorage per source. Mirrors the V4 implementation;
  // when FilterTree / BQ-view unification ships the View.columns shape
  // will subsume this, but until then localStorage is enough.
  const pinnedKey = `v3-pinned-cols:${sourceId}`;
  const [pinnedPaths, setPinnedPaths] = useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(pinnedKey);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch { return []; }
  });
  const pinnedSet = useMemo(() => new Set(pinnedPaths), [pinnedPaths]);
  const togglePinnedPath = useCallback((path: string): void => {
    setPinnedPaths(prev => {
      const next = prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path];
      try { window.localStorage.setItem(pinnedKey, JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });
  }, [pinnedKey]);
  /** Read a dot-path through a (possibly JSON-string) value. V3 row data
   *  comes from BQ where JSON columns return as strings; pinning a
   *  nested path means we extract from the raw payload string. */
  const readPinnedValue = useCallback((row: Record<string, unknown>, path: string): unknown => {
    // Path is dotted (e.g. "current.title"). First segment must exist as
    // a row column; the rest goes through the parsed object.
    const dotIdx = path.indexOf('.');
    if (dotIdx === -1) return row[path];
    const head = path.slice(0, dotIdx);
    const tail = path.slice(dotIdx + 1);
    let cur: unknown = row[head];
    if (typeof cur === 'string') {
      try { cur = JSON.parse(cur); } catch { return undefined; }
    }
    for (const seg of tail.split('.')) {
      if (cur === null || cur === undefined || typeof cur !== 'object') return undefined;
      cur = (cur as Record<string, unknown>)[seg];
    }
    return cur;
  }, []);

  // Active tab resolution priority (audit finding IA.2):
  //   1. `?tab=<name>` URL param (sharable / bookmark) wins
  //   2. localStorage prefs lastActiveTab — so operator returns to
  //      their curated view between sessions
  //   3. `events` base view default
  const tabFromUrl = searchParams.get('tab');
  const initialBaseView = (() => {
    if (tabFromUrl && views.some(v => v.name === tabFromUrl)) return tabFromUrl;
    const persisted = readPrefs(`v3.prefs.${sourceId}`).lastActiveTab;
    if (persisted && views.some(v => v.name === persisted)) return persisted;
    return views.find(v => v.name === 'events')?.name ?? views[0]?.name ?? 'events';
  })();
  const [baseView, setBaseView] = useState<string>(initialBaseView);
  const [filters, setFilters] = useState<FilterRow[]>([]);
  const [uniqueByPath, setUniqueByPath] = useState('');
  const [uniqueOrderBy, setUniqueOrderBy] = useState<'received_at_desc' | 'received_at_asc'>('received_at_desc');
  const [schema, setSchema] = useState<SchemaField[] | null>(null);

  const [previewRows, setPreviewRows] = useState<Array<Record<string, unknown>> | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const lastRowCountRef = useRef<number>(0);

  // Query-perf instrumentation. Client-side round-trip ms only — does
  // NOT include BigQuery's slot time / bytes processed (those need a
  // backend-side passthrough of JobStatistics, captured as a follow-up
  // in BACKLOG.md). Tracked rolling: last + avg + min + max + count
  // for the current view+sql session. Reset when SQL changes so the
  // operator's stats reflect the current slice, not historical noise.
  const queryStatsRef = useRef<{ last: number; total: number; min: number; max: number; count: number }>({
    last: 0, total: 0, min: Infinity, max: 0, count: 0,
  });
  // KPI #9: surface BigQuery JobStatistics inline so SRE can see
  // real cost dimensions (bytes processed, cache hit, slot ms)
  // without leaving the page. Backend returns these in
  // previewBqView's response when bq.createQueryJob succeeds.
  const [lastJobStats, setLastJobStats] = useState<{ bytes_processed: number; cache_hit: boolean; slot_ms: number } | null>(null);
  const [, setStatsRev] = useState(0); // force re-render after a stat update
  function recordQueryDuration(ms: number): void {
    const s = queryStatsRef.current;
    s.last = ms;
    s.total += ms;
    s.count += 1;
    if (ms < s.min) s.min = ms;
    if (ms > s.max) s.max = ms;
    setStatsRev(r => r + 1);
  }
  // Reset session stats when SQL changes meaningfully — avg over
  // 'previous filter' state pollutes the operator's read of the
  // current shape's perf.
  useEffect(() => {
    queryStatsRef.current = { last: 0, total: 0, min: Infinity, max: 0, count: 0 };
  }, [sourceId, baseView]);
  // Track which event_ids are "new since last fetch" so the table can
  // highlight ALL of them (not just the top), then fade after 2s.
  const seenEventIdsRef = useRef<Set<string>>(new Set());
  const [newEventIds, setNewEventIds] = useState<Set<string>>(new Set());

  const [showSavePanel, setShowSavePanel] = useState(false);
  // The simple Save dialog asks one question (name). The advanced
  // Save panel still exists for stream-action wiring (forward, dedupe,
  // enrich) — operator opens it from the simple dialog's "Advanced…"
  // link. Two surfaces, one underlying handleSave.
  const [showSaveSimple, setShowSaveSimple] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [streamAction, setStreamAction] = useState<StreamAction>('none');
  const [forwardUrl, setForwardUrl] = useState('');
  const [forwardMethod, setForwardMethod] = useState<'POST' | 'PUT'>('POST');
  const [enrichUrl, setEnrichUrl] = useState('');
  const [enrichIdPath, setEnrichIdPath] = useState('');
  const [enrichMergePath, setEnrichMergePath] = useState('enrichment');
  const [dedupeWindowSec, setDedupeWindowSec] = useState(3600);
  const [submitting, setSubmitting] = useState(false);

  // Schema fetch — once on mount + after refresh. We use the legacy
  // flat shape (?flat=true) here because V3 turns these field paths
  // into BQ column aliases via JSON_VALUE projections. BQ rejects
  // dotted names like "matches_filters.current" so the post-2026-05-12
  // recursive default schema would fail with "Invalid field name".
  // NestedCell renders nested shapes per-cell directly from cellValue,
  // so it doesn't depend on the schema entries having depth-2+ paths.
  useEffect(() => {
    getEventsSchemaFlat(sourceId).then(r => setSchema(r.fields)).catch(() => setSchema([]));
  }, [sourceId, refreshKey]);

  // Reset baseView when the active tab disappears (e.g. after delete).
  useEffect(() => {
    if (!views.some(v => v.name === baseView)) {
      setBaseView(views.find(v => v.name === 'events')?.name ?? views[0]?.name ?? 'events');
    }
  }, [views, baseView]);

  // Persist active tab to URL so deep links round-trip. We only sync
  // ?tab= when it's something other than the default `events` so
  // operator's clean URLs stay clean.
  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (baseView === 'events') next.delete('tab');
      else next.set('tab', baseView);
      return next;
    }, { replace: true });
  }, [baseView, setSearchParams]);

  // Clear stale rows when switching tabs so the operator sees
  // "refreshing…" instead of the previous view's data flicker. Without
  // this, the table renders the old `fileovi` rows for the duration of
  // the debounce + fetch round-trip.
  useEffect(() => {
    setPreviewRows(null);
    lastRowCountRef.current = 0;
  }, [baseView]);

  const hasRecipeEdits = filters.some(f => f.field_path.trim()) || uniqueByPath.trim().length > 0;

  // Persisted preferences. We persist three booleans + a per-(source,
  // tab) hidden-column set so operators don't redo their layout every
  // page load. Filters/sort intentionally NOT persisted — returning
  // to a source filtered to "0 rows" without warning is worse than
  // re-clicking a filter chip.
  const prefsKey = `v3.prefs.${sourceId}`;
  const persistedPrefs = readPrefs(prefsKey);

  // Show envelope (event_id, source_id, payload, …) as raw columns?
  // Default: false — payload fields project as named columns, envelope
  // hidden to keep the operator's actual data in the foreground.
  // Toggleable so power users can still see the system fields.
  const [showSystemFields, setShowSystemFields] = useState(persistedPrefs.showSystemFields ?? false);

  // Schema sidebar is opt-in. The auto-projected payload columns
  // already act as a discovery surface (operator sees field names in
  // the table header), so the sidebar's "click to add is_present
  // filter" affordance is mostly redundant. Operators who want
  // occurrence counts + type chips can flip this on; default off
  // keeps the table full-width.
  const [showSchemaSidebar, setShowSchemaSidebar] = useState(persistedPrefs.showSchemaSidebar ?? false);

  // Column-level sort. null sortColumn means default (received_at DESC).
  // Click a column header → cycles ASC → DESC → null. Persists per-tab
  // in component state so switching tabs resets to default.
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('DESC');

  // Right-click context menu on column headers. Quick access to
  // sort / hide / add-filter without crossing the table to the toolbar.
  // Anchored to viewport coords from the contextmenu event so the menu
  // pops up exactly where the cursor was.
  const [headerMenu, setHeaderMenu] = useState<{ col: string; left: number; top: number } | null>(null);
  useEffect(() => {
    if (!headerMenu) return;
    function close(): void { setHeaderMenu(null); }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [headerMenu]);

  // Per-tab hidden columns. Auto-projected payload schemas can push the
  // table to 15+ columns when the source has rich payloads — operator
  // wants to hide the ones that aren't load-bearing for the current
  // task. Persists per (source, tab) — switching tabs swaps the hidden
  // set in/out, so each view remembers its own column layout.
  const hiddenKey = `${baseView}__hidden`;
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() =>
    new Set(persistedPrefs.hiddenByTab?.[hiddenKey] ?? []),
  );
  // Restore the hidden set on tab switch from persisted state. Without
  // this, switching back to a tab forgets the layout the operator had.
  useEffect(() => {
    const prefs = readPrefs(prefsKey);
    setHiddenColumns(new Set(prefs.hiddenByTab?.[hiddenKey] ?? []));
  }, [baseView, prefsKey, hiddenKey]);
  // Reset hidden cols when system-fields toggle changes the column
  // universe — most operators won't expect a column they hid in
  // payload-only mode to stay hidden in system-fields mode.
  const previousSystemFieldsRef = useRef(showSystemFields);
  useEffect(() => {
    if (previousSystemFieldsRef.current !== showSystemFields) {
      setHiddenColumns(new Set());
      previousSystemFieldsRef.current = showSystemFields;
    }
  }, [showSystemFields]);

  // Write prefs back to localStorage whenever the user-facing toggles
  // change. We rebuild the per-tab hidden map from the current state;
  // tabs the operator hasn't visited keep their previously-saved sets.
  useEffect(() => {
    const prev = readPrefs(prefsKey);
    const hiddenByTab = { ...(prev.hiddenByTab ?? {}) };
    if (hiddenColumns.size === 0) delete hiddenByTab[hiddenKey];
    else hiddenByTab[hiddenKey] = [...hiddenColumns];
    writePrefs(prefsKey, {
      showSystemFields,
      showSchemaSidebar,
      hiddenByTab,
      lastActiveTab: baseView,
    });
  }, [showSystemFields, showSchemaSidebar, hiddenColumns, hiddenKey, prefsKey, baseView]);
  // Select Fields drawer (BigQuery-style) — operator opens it from the
  // table card's `columns` button. Slides in from the right with:
  //  - filter input (matches against name + type + origin)
  //  - filter-by dropdown: any / name / type / origin
  //  - per-field row: checkbox · field name · type chip · origin badge
  //    (payload / envelope / system) · occurrence% · sample value
  //  - Save / Cancel
  // Editing happens against a draft copy; Save commits, Cancel discards.
  const [showFieldsDrawer, setShowFieldsDrawer] = useState(false);
  // A11Y.5 — restore focus to whatever the operator was on when each
  // modal/drawer/dialog closes. One hook call per open-flag boolean.
  useFocusRestore(showFieldsDrawer);
  const [draftHidden, setDraftHidden] = useState<Set<string>>(new Set());
  const [fieldFilter, setFieldFilter] = useState('');
  const [fieldFilterMode, setFieldFilterMode] = useState<'any' | 'name' | 'type' | 'origin'>('any');
  function openFieldsDrawer(): void {
    setDraftHidden(new Set(hiddenColumns));
    setFieldFilter('');
    setShowFieldsDrawer(true);
  }
  function commitFieldsDrawer(): void {
    setHiddenColumns(draftHidden);
    setShowFieldsDrawer(false);
  }
  function cancelFieldsDrawer(): void {
    setShowFieldsDrawer(false);
  }
  function toggleColumnHidden(col: string): void {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col); else next.add(col);
      return next;
    });
  }

  // Track which row the cursor is over to show row-level action
  // buttons (KPI #5: 1-click replay).
  const [hoverRowId, setHoverRowId] = useState<string | null>(null);
  const [replayingId, setReplayingId] = useState<string | null>(null);
  async function replayOne(eventId: string): Promise<void> {
    setReplayingId(eventId);
    try {
      await reprocessEvent(sourceId, eventId);
      toast.show(`Replayed ${eventId.slice(0, 12)}…`, { kind: 'success' });
    } catch (e) {
      toast.show(`Replay failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setReplayingId(null);
    }
  }

  // Multi-row selection. The operator picks rows via checkboxes — once
  // any are selected, an action bar floats above the table with batch
  // operations (copy IDs, export selected as CSV/NDJSON, narrow lineage
  // exploration). Selection lives outside `previewRows` so a refresh
  // doesn't lose the operator's checks; we filter against the current
  // page's event_ids in the action bar's labels.
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
  // Drop selections that no longer match a visible row when the SQL
  // changes — otherwise the count stays "5 selected" after the rows
  // disappear from the table.
  useEffect(() => {
    if (selectedEventIds.size === 0 || !previewRows) return;
    const visible = new Set(previewRows.map(r => String(r.event_id ?? '')).filter(Boolean));
    setSelectedEventIds(prev => {
      const next = new Set([...prev].filter(id => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [previewRows]); // eslint-disable-line react-hooks/exhaustive-deps
  function toggleRowSelected(eventId: string): void {
    setSelectedEventIds(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId); else next.add(eventId);
      return next;
    });
  }
  function toggleAllSelected(): void {
    if (!previewRows) return;
    const visibleIds = previewRows.map(r => String(r.event_id ?? '')).filter(Boolean);
    setSelectedEventIds(prev => {
      const allSelected = visibleIds.length > 0 && visibleIds.every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      }
      return new Set([...prev, ...visibleIds]);
    });
  }

  // Filter-editor density. With 3+ filters the row-by-row editor turns
  // into a wall, so we collapse into compact chips by default and let
  // the operator click "edit" to expand the full inline form. The
  // operator's actual recipe (filters/uniqueByPath) lives in state
  // independent of this — it's purely a presentational toggle.
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const filtersCompact = filters.length >= 3 && !filtersExpanded;

  // Hover-preview popover for cells whose displayed value is truncated.
  // The table truncates JSON objects, long strings, and `payload` to a
  // single line. On hover, we float a popover with the pretty-printed
  // full value so the operator can scan a payload without opening the
  // lineage drawer. Delays the show by 250ms to avoid flicker on fast
  // cursor sweeps across the table.
  const [hoverPreview, setHoverPreview] = useState<{
    rowIdx: number; col: string; left: number; top: number;
  } | null>(null);
  const hoverTimerRef = useRef<number | null>(null);
  function scheduleHoverPreview(rowIdx: number, col: string, anchor: HTMLElement): void {
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    const rect = anchor.getBoundingClientRect();
    hoverTimerRef.current = window.setTimeout(() => {
      setHoverPreview({ rowIdx, col, left: rect.left, top: rect.bottom + 4 });
    }, 250);
  }
  function cancelHoverPreview(): void {
    if (hoverTimerRef.current) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHoverPreview(null);
  }
  // Close on Escape so a stuck popover never blocks the operator.
  useEffect(() => {
    if (!hoverPreview) return;
    function onKey(e: KeyboardEvent): void { if (e.key === 'Escape') cancelHoverPreview(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [hoverPreview]);

  function cycleSort(column: string): void {
    if (sortColumn !== column) { setSortColumn(column); setSortDir('ASC'); return; }
    if (sortDir === 'ASC') { setSortDir('DESC'); return; }
    setSortColumn(null); setSortDir('DESC');
  }
  // Reset sort when switching base view — column names differ across views.
  useEffect(() => { setSortColumn(null); setSortDir('DESC'); }, [baseView]);

  // Pause auto-refresh while the operator is interacting with the
  // surface — pointer hovering the table, input focused, rows
  // selected, or scrolled away from the top. The 10s polling that
  // replaces previewRows from under the cursor was disorienting:
  // hover popovers dismissed, the row the operator was reading
  // shifted as new rows arrived above, and an in-progress filter
  // edit could be interrupted by a re-fetch flicker.
  // Resume polling the moment the cursor leaves and focus returns to
  // body — operators get fresh data when they're not in the middle
  // of something.
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const interactionRef = useRef(false);
  const [isInteracting, setIsInteracting] = useState(false);
  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;
    let pointerOver = false;
    let focusInside = false;
    let scrolled = false;
    const update = (): void => {
      const next = pointerOver || focusInside || scrolled;
      if (interactionRef.current === next) return;
      interactionRef.current = next;
      setIsInteracting(next);
    };
    const onEnter = (): void => { pointerOver = true; update(); };
    const onLeave = (): void => { pointerOver = false; update(); };
    const onFocusIn = (): void => { focusInside = true; update(); };
    const onFocusOut = (): void => { focusInside = false; update(); };
    surface.addEventListener('pointerenter', onEnter);
    surface.addEventListener('pointerleave', onLeave);
    surface.addEventListener('focusin', onFocusIn);
    surface.addEventListener('focusout', onFocusOut);
    let detachScroll: (() => void) | null = null;
    // Scroll listener attaches to the table's scroll wrapper. The
    // wrapper mounts after first preview rows arrive — re-bind via
    // requestAnimationFrame after each render until ref is set.
    const tryBindScroll = (): void => {
      const el = tableScrollRef.current;
      if (!el || detachScroll) return;
      const onScroll = (): void => {
        const wasScrolled = scrolled;
        scrolled = el.scrollTop > 50;
        if (wasScrolled !== scrolled) update();
      };
      el.addEventListener('scroll', onScroll, { passive: true });
      detachScroll = () => el.removeEventListener('scroll', onScroll);
    };
    const scrollBindInterval = setInterval(tryBindScroll, 200);
    tryBindScroll();
    return () => {
      surface.removeEventListener('pointerenter', onEnter);
      surface.removeEventListener('pointerleave', onLeave);
      surface.removeEventListener('focusin', onFocusIn);
      surface.removeEventListener('focusout', onFocusOut);
      clearInterval(scrollBindInterval);
      detachScroll?.();
    };
  }, []);

  // Free-text payload search. Translates to a single LIKE predicate on
  // TO_JSON_STRING(payload) — cheap, no schema awareness needed,
  // matches across all keys and values. Debounced via the existing
  // SQL-change debounce so each keystroke doesn't fire a fresh query.
  // Reset on tab switch since the search is "find within this view".
  const [searchText, setSearchText] = useState('');
  useEffect(() => { setSearchText(''); }, [baseView]);

  // Date-scope quick preset for the impatient operator. 'all' is the
  // default; the others apply a WHERE on the envelope `received_at`
  // column. Doesn't go through the recipe-filter system because that
  // path always wraps with JSON_VALUE(payload, '$.X') — received_at
  // is on the envelope, not the payload. We splice the predicate at
  // the FROM-wrap level alongside the search clause.
  type DateScope = 'all' | 'hour' | 'today' | '7d';
  const [dateScope, setDateScope] = useState<DateScope>('all');
  useEffect(() => { setDateScope('all'); }, [baseView]);

  // Origin filter: slice events by HOW they arrived. Two flavours:
  //  - by KIND ('webhook'/'upload'/'api_pull'/'email'/'manual') —
  //    matches `JSON_VALUE(origin, '$.kind')` so an operator picks
  //    "All webhooks" / "All file uploads" / etc.
  //  - by SPECIFIC INPUT id (`input:<input_id>`) — matches
  //    `JSON_VALUE(origin, '$.input_id')` so when a source has 2+
  //    webhooks (e.g. Stripe prod + Stripe staging) the operator
  //    can drill straight to one named hook instead of seeing the
  //    combined stream.
  // 'all' = no predicate.
  type OriginFilter = 'all' | 'webhook' | 'upload' | 'api_pull' | 'email' | 'manual' | `input:${string}`;
  const [originKind, setOriginKind] = useState<OriginFilter>('all');
  useEffect(() => { setOriginKind('all'); }, [baseView]);

  // Brand filter — set when operator clicks a per-row brand chip.
  // Resolves to that brand's signature path predicate (all required
  // paths IS NOT NULL) at the FROM-wrap level. Different from the
  // detected-models flow: this is a one-click filter, not a saved
  // view.
  const [brandFilter, setBrandFilter] = useState<string | null>(null);
  useEffect(() => { setBrandFilter(null); }, [baseView]);

  // Fingerprint filter — client-side narrowing of previewRows to those
  // sharing a structural group. Operator clicks a `grp_…` cell in the
  // `_fingerprint` column → only same-shape rows remain visible. NOT
  // pushed into the BQ SQL (we don't store fingerprint per event); it
  // post-filters the in-memory preview. Clears on baseView change so
  // tab switches don't carry a stale filter.
  const [fingerprintFilter, setFingerprintFilter] = useState<string | null>(null);
  useEffect(() => { setFingerprintFilter(null); }, [baseView]);

  // Saved Schema Definitions for this source. Used to resolve a row's
  // fingerprint to the operator's chosen schema name (when one
  // exists). Unmatched fingerprints fall back to a deterministic
  // color-noun label (resolveFingerprintLabel). Refetched on every
  // refreshKey bump so a freshly-promoted schema label shows up
  // without a hard reload.
  const [savedSchemas, setSavedSchemas] = useState<SchemaDefinition[]>([]);
  useEffect(() => {
    let alive = true;
    listSchemaDefs(sourceId)
      .then(defs => { if (alive) setSavedSchemas(defs); })
      .catch(() => { if (alive) setSavedSchemas([]); });
    return () => { alive = false; };
  }, [sourceId, refreshKey]);

  // grp_xxx → SchemaDefinition lookup. Built once per render from the
  // saved-schema list; cheap (Map construction over ≤ 50 schemas).
  const schemaByFingerprint = useMemo(() => {
    const m = new Map<string, { name: string }>();
    for (const s of savedSchemas) {
      for (const fp of s.source_fingerprints ?? []) {
        if (fp && !m.has(fp)) m.set(fp, { name: s.name });
      }
    }
    return m;
  }, [savedSchemas]);

  // Group-by pivot. When set, the main SQL flips from row-projection
  // to a GROUP BY count over the chosen field. Tabla render switches
  // to 2-column Value | Count layout so the operator gets dashboard-
  // level insight (top values + percentages) without writing SQL.
  // Drill-down: click a Value cell → set eq filter on this field +
  // clear groupBy → back to detail view filtered to that value.
  const [groupByField, setGroupByField] = useState<string | null>(null);
  // After a drill from pivot, remember the previous pivot+filters
  // state so the operator can hop back at any time (audit finding
  // UX.4 — toast-only undo lasts 4s; inline chip persists).
  const [drillback, setDrillback] = useState<{
    field: string;
    value: string;
    prevFilters: FilterRow[];
  } | null>(null);
  // Clear drillback when the operator manually changes filters or
  // re-enters pivot mode — the previous-state snapshot would be stale.
  useEffect(() => { if (groupByField) setDrillback(null); }, [groupByField]);
  const [showGroupByPicker, setShowGroupByPicker] = useState(false);
  const groupByPickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showGroupByPicker) return;
    function close(e: MouseEvent): void {
      if (groupByPickerRef.current && !groupByPickerRef.current.contains(e.target as Node)) setShowGroupByPicker(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showGroupByPicker]);
  useEffect(() => { setGroupByField(null); }, [baseView]);

  // 24h volume histogram above the table — 24 hourly buckets,
  // re-queried whenever the operator's slice changes (filters /
  // search / brand / origin). Renders as a compact bar row that
  // doubles as a click-target: clicking a bar narrows dateScope
  // to that hour.
  //
  // Cached in memory for 5 min per cache-key (audit finding PERF.2 +
  // COST.1). The same slice fired 3-5 times in a typical operator
  // session: filter add → cell click → manual refresh → … each was
  // re-querying. With cache: first hit primes, subsequent reads in
  // the window are instant. Cache key includes every input that
  // contributes to the SQL.
  const [histogramRows, setHistogramRows] = useState<Array<{ ts: number; count: number }> | null>(null);
  /** Build the SQL predicate for the current origin filter. Handles
   *  both kind-level ('webhook'/'upload'/...) and specific-input
   *  ('input:inp_xyz') variants. Input ids match
   *  /^inp_[a-zA-Z0-9_]+$/ so a regex gate keeps malformed values
   *  from sneaking into JSON_VALUE — defence-in-depth even though
   *  the filter state is internal. */
  function originPredicateSql(filter: OriginFilter): string {
    if (filter.startsWith('input:')) {
      const id = filter.slice('input:'.length);
      if (!/^[a-zA-Z0-9_]+$/.test(id)) return '1=1';
      return `JSON_VALUE(origin, '$.input_id') = '${id}'`;
    }
    if (/^[a-z_]+$/.test(filter)) {
      return `JSON_VALUE(origin, '$.kind') = '${filter}'`;
    }
    return '1=1';
  }

  useEffect(() => {
    // Histogram tracks events tab only — custom views often don't
    // have a received_at column with the same semantics.
    if (baseView !== 'events') { setHistogramRows(null); return; }
    let alive = true;
    // Paired predicates: the DATE filter enables Hive partition pruning
    // on the T2 external table (10-30× speed-up on 50k-object sources),
    // the TIMESTAMP filter does the precise hour-level cut. 2-day DATE
    // window covers any timezone offset on a 24-hour scan.
    const histPredicates: string[] = [
      `received_date >= DATE_SUB(CURRENT_DATE(), INTERVAL 2 DAY)`,
      `received_at > TIMESTAMP_SUB(CURRENT_TIMESTAMP(), INTERVAL 24 HOUR)`,
    ];
    // Apply same slicing as main view so the histogram tracks what
    // the operator is looking at — except dateScope (would shrink
    // the histogram window itself; we want the always-24h frame).
    if (searchText.trim()) {
      const escaped = searchText.trim().toLowerCase().replace(/'/g, "\\'");
      histPredicates.push(`LOWER(TO_JSON_STRING(payload)) LIKE '%${escaped}%'`);
    }
    if (originKind !== 'all') {
      histPredicates.push(originPredicateSql(originKind));
    }
    if (brandFilter) {
      const bp = buildBrandFilterPredicate(brandFilter);
      if (bp) histPredicates.push(bp);
    }
    const filterPreds = filters
      .filter(f => f.field_path.trim())
      .map(f => filterRowToPredicate(f))
      .filter((s): s is string => s !== null);
    histPredicates.push(...filterPreds);
    const histSql = `SELECT TIMESTAMP_TRUNC(received_at, HOUR) AS bucket, COUNT(*) AS count\nFROM \`${dataset}.${baseView}\`\nWHERE ${histPredicates.join(' AND ')}\nGROUP BY 1\nORDER BY 1`;
    // Cache lookup — skip the BQ round-trip if the same SQL was
    // queried within the last 5 min.
    const cacheKey = `${sourceId}::${histSql}`;
    const cached = histogramCache.get(cacheKey);
    if (cached && Date.now() - cached.at < 5 * 60 * 1000) {
      setHistogramRows(cached.rows);
      return () => { alive = false; };
    }
    previewBqView(sourceId, histSql, 50)
      .then(r => {
        if (!alive) return;
        const rows = r.rows.map(row => ({
          ts: typeof row.bucket === 'string' ? Date.parse(row.bucket) : 0,
          count: Number(row.count) || 0,
        }));
        histogramCache.set(cacheKey, { rows, at: Date.now() });
        setHistogramRows(rows);
      })
      .catch(() => { if (alive) setHistogramRows(null); });
    return () => { alive = false; };
  }, [sourceId, dataset, baseView, searchText, originKind, brandFilter, filters, refreshKey]);

  // Field-value distribution side drawer. Independent of the main
  // table — fires its own preview query against a GROUP BY SQL when
  // a column is selected. Operator triggers via right-click on a
  // header → "📊 Show distribution". Drawer rows render a horizontal
  // bar showing % share + click-to-filter. "Open as pivot" link
  // switches to groupByField mode for full-table view.
  const [distributionField, setDistributionField] = useState<string | null>(null);
  const [distributionRows, setDistributionRows] = useState<Array<{ value: string; count: number }> | null>(null);
  const [distributionLoading, setDistributionLoading] = useState(false);
  const [distributionDurationMs, setDistributionDurationMs] = useState<number | null>(null);
  useEffect(() => {
    if (!distributionField) { setDistributionRows(null); setDistributionDurationMs(null); return; }
    setDistributionLoading(true);
    setDistributionRows(null);
    setDistributionDurationMs(null);
    const expr = fieldExpr(distributionField);
    const distSql = `SELECT ${expr} AS \`value\`, COUNT(*) AS \`count\`\nFROM \`${dataset}.${baseView}\`\nWHERE ${expr} IS NOT NULL\nGROUP BY 1\nORDER BY \`count\` DESC\nLIMIT 50`;
    const start = performance.now();
    previewBqView(sourceId, distSql, 50)
      .then(r => {
        setDistributionDurationMs(Math.round(performance.now() - start));
        setDistributionRows(r.rows.map(row => ({
          value: String(row.value ?? ''),
          count: Number(row.count) || 0,
        })));
        setDistributionLoading(false);
      })
      .catch(e => {
        toast.show(`Distribution query failed: ${(e as Error).message}`, { kind: 'error' });
        setDistributionField(null);
        setDistributionLoading(false);
      });
    // toast intentionally omitted (memoized stable ref upstream)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributionField, dataset, baseView, sourceId]);

  const payloadPaths = useMemo(
    () => (schema ?? [])
      .filter(f => f.path !== 'payload' && f.path !== 'origin' && f.path !== 'event_id'
        && f.path !== 'event_key' && f.path !== 'source_id' && f.path !== 'received_at'
        && f.path !== 'parent_event_id' && f.path !== 'derivation_depth' && f.path !== 'trace_id')
      .map(f => f.path),
    [schema],
  );

  const sql = useMemo(() => {
    const orderBy = sortColumn
      ? `ORDER BY \`${sortColumn}\` ${sortDir}`
      : 'ORDER BY received_at DESC';
    // Build base-table predicates: search + dateScope + recipe filters.
    // All apply at the FROM-level so columns like `payload` and
    // `received_at` are in scope. Critically: keeping recipe filters
    // here (not in buildRecipeSql) means the auto-projected SELECT
    // stays valid — payload top-level keys remain flat columns even
    // after the operator clicks a cell to filter. Without this, a
    // single cell click flips the table into 'payload' raw-JSON mode.
    const predicates: string[] = [];
    if (searchText.trim()) {
      const escaped = searchText.trim().toLowerCase().replace(/'/g, "\\'");
      predicates.push(`LOWER(TO_JSON_STRING(payload)) LIKE '%${escaped}%'`);
    }
    if (dateScope !== 'all') {
      const intervalSql = dateScope === 'hour' ? 'INTERVAL 1 HOUR'
        : dateScope === 'today' ? 'INTERVAL 1 DAY'
        : 'INTERVAL 7 DAY';
      // Hive partition pruning on the T2 external table — the DATE
      // predicate cuts scan from "all objects under <src>/" to just
      // the relevant daily partitions. Paired with the TIMESTAMP
      // filter that does the precise window. Day-window padded +1 so
      // boundary hours don't fall outside the partition selection.
      const dayWindow = dateScope === 'hour' ? 1
        : dateScope === 'today' ? 2
        : 8;
      predicates.push(`received_date >= DATE_SUB(CURRENT_DATE(), INTERVAL ${dayWindow} DAY)`);
      predicates.push(`received_at > TIMESTAMP_SUB(CURRENT_TIMESTAMP(), ${intervalSql})`);
    }
    if (originKind !== 'all') {
      predicates.push(originPredicateSql(originKind));
    }
    if (brandFilter) {
      const brandPred = buildBrandFilterPredicate(brandFilter);
      if (brandPred) predicates.push(brandPred);
    }
    // Inline filter→SQL — mirrors recipe-sql.ts:filterToSql but stays
    // local so we can apply it to the auto-projection FROM-wrap rather
    // than going through buildRecipeSql (which would lose flat columns).
    const filterPredicates = filters
      .filter(f => f.field_path.trim())
      .map(f => filterRowToPredicate(f))
      .filter((s): s is string => s !== null);
    predicates.push(...filterPredicates);

    const fqBase = `\`${dataset}.${baseView}\``;
    const fromTarget = predicates.length > 0
      ? `(SELECT * FROM ${fqBase} WHERE ${predicates.join(' AND ')})`
      : fqBase;

    // Decision: stay in auto-projection mode when on events tab without
    // system-fields toggle, group-by, or unique-by. This is the 95%
    // path — flat columns from JSON_VALUE, all filters / search / date
    // scope baked into the FROM-wrap.
    const isBaseEvents = baseView === 'events';
    const useAutoProjection = isBaseEvents && !showSystemFields && !uniqueByPath.trim();
    let baseSql: string;
    if (useAutoProjection) {
      // Build the auto-projected SQL ourselves (don't go through
      // buildAutoProjectedSql) so we can append a `_brand` column
      // computed via SQL CASE over BRAND_RULES. Per-row brand
      // recognition surfaces as a chip in the leftmost cell — the
      // operator sees Slack / Stripe / Linear etc. without opening
      // the detected-models panel.
      const orderByLocal = sortColumn ? `ORDER BY \`${sortColumn}\` ${sortDir}` : 'ORDER BY received_at DESC';
      const brandCase = buildBrandSqlCase();
      // Per-input attribution (multi-hook feature): pull input_name +
      // input_tags out of origin so they render as columns. Operator
      // sees WHICH hook each event came from + the tags they chose
      // (env, region, integration). Tags come back as a JSON string
      // since BQ has no map type — formatCell renders them as a chip
      // row when the value is a JSON object.
      // Raw `payload` is included so the client can compute a
      // `_fingerprint` column per row (groups events by their leaf-
      // path set). Same fingerprint as views-auto-suggest server-side,
      // computed JS-side here in O(50µs/row) so we don't pay a BQ
      // round-trip. Operator sees which structural group each row
      // belongs to + can click-to-filter by that group.
      const proj = payloadPaths.length === 0
        ? `event_id, received_at, ${brandCase} AS _brand,\n  JSON_VALUE(origin, '$.input_name') AS input_name,\n  JSON_QUERY(origin, '$.input_tags') AS input_tags,\n  payload`
        : ['event_id', 'received_at', `${brandCase} AS _brand`,
            `JSON_VALUE(origin, '$.input_name') AS input_name`,
            `JSON_QUERY(origin, '$.input_tags') AS input_tags`,
            'payload',
            ...payloadPaths.map(p => `JSON_VALUE(payload, '$.${p}') AS \`${p}\``)
          ].join(',\n  ');
      baseSql = `SELECT\n  ${proj}\nFROM ${fqBase}\n${orderByLocal}`;
    } else if (!hasRecipeEdits) {
      // Custom view tab or system-fields toggle on — SELECT * and
      // trust the underlying view's projection.
      baseSql = `SELECT * FROM ${fqBase} ${orderBy}`;
    } else {
      // Recipe-builder owns the SQL — used when uniqueByPath is set
      // (LWW dedupe needs ROW_NUMBER + PARTITION BY which lives there).
      baseSql = `${buildRecipeSql({ dataset, baseView, picks: [], filters, uniqueByPath, uniqueOrderBy })}\n${orderBy}`;
    }
    // Group-by pivot mode short-circuits the projection logic
    // entirely. The aggregate SELECT references `payload` directly
    // from the base table — wrapping the auto-projected SELECT (which
    // exposes flat columns only) would fail with 'Unrecognized name:
    // payload at …'. Instead we issue a clean GROUP BY against the
    // base view, ANDing the field's IS NOT NULL into existing
    // predicates so all filters / search / dateScope still apply.
    if (groupByField) {
      const expr = fieldExpr(groupByField);
      const aggregatePredicates = [...predicates, `${expr} IS NOT NULL`];
      const where = aggregatePredicates.length > 0 ? `WHERE ${aggregatePredicates.join(' AND ')}\n` : '';
      return `SELECT ${expr} AS \`${groupByField}\`,\n  COUNT(*) AS \`count\`\nFROM ${fqBase}\n${where}GROUP BY 1\nORDER BY \`count\` DESC\nLIMIT 50`;
    }

    // Substitute the FROM target so the predicates land inside the
    // base scope. We rewrite by string replace because the builders
    // emit a single `FROM \`dataset.view\`` token; this avoids changing
    // every builder's signature for a UI-only feature.
    const composed = predicates.length > 0 ? baseSql.replace(fqBase, fromTarget) : baseSql;
    return composed;
  }, [dataset, baseView, filters, uniqueByPath, uniqueOrderBy, hasRecipeEdits, showSystemFields, payloadPaths, sortColumn, sortDir, searchText, dateScope, groupByField, originKind, brandFilter]);

  // Debounced preview re-fetch. Triggered by SQL change OR refresh bump
  // OR auto-refresh interval. Detects "new top row" via row_count delta
  // and pulses the table briefly + toasts when new arrivals come in
  // via auto-refresh (not during recipe edits).
  const previousSqlRef = useRef<string>(sql);
  useEffect(() => {
    setPreviewLoading(true);
    setPreviewError(null);
    const sqlChanged = previousSqlRef.current !== sql;
    previousSqlRef.current = sql;
    const handle = setTimeout(() => {
      const queryStart = performance.now();
      previewBqView(sourceId, sql, 50)
        .then(r => {
          const ms = Math.round(performance.now() - queryStart);
          recordQueryDuration(ms);
          if (r.job_stats) setLastJobStats(r.job_stats);
          const incomingIds = new Set(
            r.rows.map(row => String(row.event_id ?? '')).filter(Boolean),
          );
          // Diff against the last seen set: anything in incoming AND
          // NOT in the seen set is a "new arrival" (only meaningful
          // after the first fetch, otherwise everything looks "new").
          const isFirstFetch = seenEventIdsRef.current.size === 0;
          const newlyArrived = isFirstFetch
            ? new Set<string>()
            : new Set([...incomingIds].filter(id => !seenEventIdsRef.current.has(id)));

          if (newlyArrived.size > 0 && !sqlChanged) {
            // Visual highlight only — no toast. The amber row tint is
            // already a clear signal, and toasts on every poll cycle
            // were both noisy and (when Toast Context wasn't memoized)
            // a re-render trigger. Keep the surface quiet.
            setNewEventIds(newlyArrived);
            // Fade highlight after 2s so subsequent refreshes don't
            // keep the same rows highlighted indefinitely.
            setTimeout(() => setNewEventIds(new Set()), 2000);
          } else if (sqlChanged) {
            // Recipe edit — suppress arrival highlights.
            setNewEventIds(new Set());
          }

          seenEventIdsRef.current = incomingIds;
          lastRowCountRef.current = r.rows.length;
          // Enrich each row with its schema fingerprint computed client-
          // side from the raw `payload` column. Operator sees which
          // structural group every row belongs to + can click-to-filter
          // by it. Mutation is intentional — the BQ result rows are
          // owned by this component and a useMemo derive would add a
          // useless re-allocation per render.
          for (const row of r.rows) {
            row._fingerprint = computeRowFingerprint(row.payload);
          }
          setPreviewRows(r.rows);
          setPreviewLoading(false);
          setLastRefreshAt(Date.now());
        })
        .catch(e => { setPreviewError(String(e)); setPreviewLoading(false); });
    }, 200);
    return () => clearTimeout(handle);
    // toast intentionally omitted — it's stable now (memoized in
    // Toast.tsx) but listing it would still re-fire this effect on
    // any cross-tree toast call. show/confirm are captured at use
    // time and remain valid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, sql, refreshKey]);

  // Keyboard shortcuts. Skipped when focus is inside an input/textarea
  // so the operator can still type filter values normally. The toolbar
  // shows a `?` button that lists what's wired.
  const [showShortcuts, setShowShortcuts] = useState(false);
  useFocusRestore(showShortcuts);
  useFocusRestore(showSavePanel);
  useFocusRestore(showSaveSimple);
  useFocusRestore(distributionField !== null);
  useFocusRestore(headerMenu !== null);
  useEffect(() => {
    function isTypingTarget(t: EventTarget | null): boolean {
      if (!t || !(t instanceof HTMLElement)) return false;
      const tag = t.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable;
    }
    function onKey(e: KeyboardEvent): void {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (e.key === 'r') {
        e.preventDefault();
        if (!previewLoading) setRefreshKey(k => k + 1);
      } else if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        setShowShortcuts(s => !s);
      } else if (e.key === 'Escape') {
        if (showShortcuts) { setShowShortcuts(false); return; }
        if (distributionField) { setDistributionField(null); return; }
        if (groupByField) { setGroupByField(null); return; }
        if (fingerprintFilter) { setFingerprintFilter(null); return; }
        if (selectedEventIds.size > 0) { setSelectedEventIds(new Set()); return; }
        if (fullscreen) { setFullscreen(false); return; }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [previewLoading, showShortcuts, selectedEventIds.size, distributionField, groupByField, fingerprintFilter, fullscreen]);

  // Track wall-clock of the last successful refresh so we can render a
  // "Last refreshed N seconds ago" indicator that auto-ticks. Cost
  // audit #18: only run the per-second re-render loop when we
  // actually have a timestamp to age AND the tab is visible. Saves
  // 60 re-renders / minute / hidden tab.
  const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null);
  const [, setRefreshTick] = useState(0);
  useEffect(() => {
    if (lastRefreshAt === null) return;
    const h = setInterval(() => {
      if (!document.hidden) setRefreshTick(t => t + 1);
    }, 1000);
    return () => clearInterval(h);
  }, [lastRefreshAt]);

  // Auto-refresh ticker — bumps refreshKey every AUTO_REFRESH_MS so the
  // preview re-fetches even when the SQL hasn't changed. Skipped:
  //  · while a fetch is already in flight (avoid stacking)
  //  · while the tab is hidden (visibility-aware)
  //  · while the operator is interacting with the surface (don't yank
  //    rows out from under their cursor / focused input / scroll
  //    position — see surfaceRef block above)
  // On regain-visibility we kick a single refresh immediately so the
  // operator sees fresh data instead of the up-to-60s-stale snapshot.
  useEffect(() => {
    if (refreshIntervalMs <= 0) return; // Manual-only
    const h = setInterval(() => {
      if (document.hidden) return;
      if (interactionRef.current) return;
      if (!previewLoading) setRefreshKey(k => k + 1);
    }, refreshIntervalMs);
    function onVisibility(): void {
      if (!document.hidden && !previewLoading && !interactionRef.current) setRefreshKey(k => k + 1);
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => { clearInterval(h); document.removeEventListener('visibilitychange', onVisibility); };
  }, [previewLoading, refreshIntervalMs]);

  // Compute progress 0..1 for the next auto-refresh based on wall-clock
  // since the last successful fetch. Used to drive a thin progress bar
  // under the refresh icon so the operator knows "polling is active."
  const refreshProgress = lastRefreshAt && refreshIntervalMs > 0
    ? Math.min(1, (Date.now() - lastRefreshAt) / refreshIntervalMs)
    : 0;

  function addFilter(): void { setFilters(prev => [...prev, { field_path: '', op: 'is_present', value: '' }]); }
  function updateFilter(idx: number, patch: Partial<FilterRow>): void {
    setFilters(prev => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));
  }
  function removeFilter(idx: number): void { setFilters(prev => prev.filter((_, i) => i !== idx)); }
  function clearRecipe(): void {
    setFilters([]); setUniqueByPath(''); setUniqueOrderBy('received_at_desc');
  }

  /** Click a payload field in the schema sidebar → add as a presence
   *  filter (`is_present`) so the operator immediately sees only events
   *  with that field. Re-clicking removes it. */
  function toggleSchemaFilter(path: string): void {
    setFilters(prev => {
      const existing = prev.find(f => f.field_path === path && f.op === 'is_present');
      if (existing) return prev.filter(f => f !== existing);
      return [...prev, { field_path: path, op: 'is_present', value: '' }];
    });
  }

  // Top-level event-envelope columns that don't make sense as `eq`
  // filters within a per-source view: source_id is constant, hash IDs
  // are unique per row (filter would always narrow to 1), payload + origin
  // are JSON aggregates that need a different drill path. received_at
  // is excluded because timestamps are sub-second unique — `eq` on a
  // timestamp would narrow to one event, never useful for browsing.
  const NON_FILTERABLE_CELLS = new Set([
    'source_id', 'event_key', 'parent_event_id', 'received_at',
    'derivation_depth', 'trace_id', 'payload', 'origin',
    // input_tags is a JSON object — eq-on-stringified-JSON is useless;
    // input_name CAN be filtered but the recipe-filter system targets
    // payload paths only, so clicking would mis-route. Block both;
    // operator filters by tag using the dedicated brand chip / Origin
    // dropdown in the toolbar.
    'input_tags', 'input_name',
  ]);

  /** Click a CELL value in the table:
   *   - event_id        → open the lineage drawer for that event
   *   - envelope cells  → no-op (would always narrow to 1 row or null)
   *   - everything else → pin as `eq` filter so the operator narrows
   *     the table to matching rows
   *
   *   Snapshots the previous filter list so the toast can offer Undo —
   *   common case: operator click-narrows the wrong row, recovers
   *   without re-finding their previous state.
   */
  function handleCellClick(field: string, value: unknown): void {
    if (value === null || value === undefined) return;
    // Pivot drill-down: in group-by mode, clicking the value cell of
    // a pivot row drills down to events matching that value. Set an
    // eq filter on the grouped field and exit pivot — operator goes
    // from "5 reaction_added events" summary back to the actual rows.
    // Count column is non-filterable (clicking the count makes no
    // sense — it's a derived aggregate, not a payload value).
    if (groupByField && field === groupByField) {
      const strVal = typeof value === 'string' ? value : String(value);
      const prevPivotField = groupByField;
      const prevFilters = filters;
      setFilters(prev => {
        const idx = prev.findIndex(f => f.field_path === field && f.op === 'eq');
        if (idx >= 0) return prev.map((f, i) => i === idx ? { ...f, value: strVal } : f);
        return [...prev, { field_path: field, op: 'eq', value: strVal }];
      });
      setGroupByField(null);
      // Persistent drill-back chip — survives beyond toast TTL.
      setDrillback({ field: prevPivotField, value: strVal, prevFilters });
      toast.show(`Drilled into ${field}=${strVal.length > 30 ? strVal.slice(0, 30) + '…' : strVal}`, { kind: 'success' });
      return;
    }
    if (groupByField && field === 'count') return; // count is non-filterable
    if (field === 'event_id' && typeof value === 'string') {
      setLineageEventId(value);
      return;
    }
    if (NON_FILTERABLE_CELLS.has(field)) return;
    const strVal = typeof value === 'string' ? value : String(value);
    const prevFilters = filters;
    setFilters(prev => {
      const existing = prev.findIndex(f => f.field_path === field && f.op === 'eq');
      if (existing >= 0) {
        return prev.map((f, i) => i === existing ? { ...f, value: strVal } : f);
      }
      return [...prev, { field_path: field, op: 'eq', value: strVal }];
    });
    toast.show(
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span>Filter added: <code style={{ fontFamily: 'var(--mono)' }}>{field}</code> = <code style={{ fontFamily: 'var(--mono)' }}>{strVal.length > 40 ? `${strVal.slice(0, 40)}…` : strVal}</code></span>
        <button
          type="button"
          onClick={() => { setFilters(prevFilters); toast.show('Filter undone', { kind: 'info', ttlMs: 1500 }); }}
          style={{
            padding: '2px 10px', fontSize: 11, fontFamily: 'var(--mono)',
            background: 'transparent', border: '1px solid var(--accent-line)',
            color: 'var(--accent)', cursor: 'pointer', borderRadius: 3,
          }}
        >undo</button>
      </span>,
      { kind: 'success' },
    );
  }

  /** Compose a smart default name from the operator's current slicing
   *  state. Patterns:
   *   - Single eq filter `event=meeting.started` → `event_meeting_started`
   *   - Multiple filters → `event_meeting_started_and_2_more`
   *   - Search → `search_<term>`
   *   - DateScope → suffix `_today` / `_last_hour` / `_last_7d`
   *   - Sort → suffix `_by_<col>`
   *  Sanitised to letters/digits/underscore so it round-trips through
   *  the BQ view name validator without further edits. Caller still
   *  gets to override before saving. */
  function suggestViewName(): string {
    const pieces: string[] = [];
    const sanitise = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const validFilters = filters.filter(f => f.field_path.trim());
    if (validFilters.length === 1) {
      const f = validFilters[0]!;
      if (['eq', 'neq'].includes(f.op) && f.value) {
        pieces.push(sanitise(`${f.field_path}_${f.value}`).slice(0, 40));
      } else if (['is_present', 'is_empty'].includes(f.op)) {
        pieces.push(sanitise(`${f.field_path}_${f.op === 'is_present' ? 'set' : 'empty'}`));
      } else {
        pieces.push(sanitise(f.field_path));
      }
    } else if (validFilters.length > 1) {
      const first = validFilters[0]!;
      const firstName = first.value && ['eq', 'neq'].includes(first.op)
        ? sanitise(`${first.field_path}_${first.value}`).slice(0, 30)
        : sanitise(first.field_path);
      pieces.push(`${firstName}_and_${validFilters.length - 1}_more`);
    }
    if (searchText.trim()) {
      pieces.push(`search_${sanitise(searchText.trim()).slice(0, 24)}`);
    }
    if (dateScope === 'hour') pieces.push('last_hour');
    else if (dateScope === 'today') pieces.push('today');
    else if (dateScope === '7d') pieces.push('last_7d');
    if (sortColumn) {
      pieces.push(`by_${sanitise(sortColumn)}${sortDir === 'ASC' ? '_asc' : ''}`);
    }
    if (uniqueByPath.trim()) {
      pieces.push(`unique_${sanitise(uniqueByPath.trim()).slice(0, 24)}`);
    }
    if (pieces.length === 0) {
      // Bare current view (no slicing) — name it after the day so it
      // disambiguates from re-saves later.
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '_');
      return `${baseView}_snapshot_${stamp}`;
    }
    return pieces.join('__').slice(0, 60);
  }

  function buildTriggerAction(): { action: TriggerAction; needsViewScope: boolean } | null {
    if (streamAction === 'none') return null;
    if (streamAction === 'validate') {
      const fields = filters.filter(f => f.field_path.trim() && f.op === 'is_present').map(f => f.field_path.trim());
      if (fields.length === 0) {
        toast.show('Validate gate needs at least one "is present" filter row.', { kind: 'warning' });
        return null;
      }
      return { action: { type: 'validate_required', fields }, needsViewScope: false };
    }
    if (streamAction === 'dedupe') {
      if (!uniqueByPath.trim()) {
        toast.show('Dedupe gate needs the "unique on key" path set.', { kind: 'warning' });
        return null;
      }
      return {
        action: { type: 'validate_dedupe', key_path: uniqueByPath.trim(), window_sec: Number(dedupeWindowSec) || 3600 },
        needsViewScope: false,
      };
    }
    if (streamAction === 'forward') {
      if (!forwardUrl.trim()) {
        toast.show('Forward needs a target URL.', { kind: 'warning' });
        return null;
      }
      return { action: { type: 'forward_webhook', url: forwardUrl.trim(), method: forwardMethod }, needsViewScope: true };
    }
    if (streamAction === 'enrich') {
      if (!enrichUrl.trim() || !enrichIdPath.trim() || !enrichMergePath.trim()) {
        toast.show('Enrich needs URL, id_path, and merge_path.', { kind: 'warning' });
        return null;
      }
      return {
        action: { type: 'enrich_fetch', url: enrichUrl.trim(), id_path: enrichIdPath.trim(), merge_path: enrichMergePath.trim(), method: 'GET' },
        needsViewScope: true,
      };
    }
    return null;
  }

  /** One-question quick save: just create a BQ view from the current
   *  SQL. No trigger, no stream action — operator gets a tab they can
   *  come back to and edit later. Designed for the impatient/non-tech
   *  path where the goal is "make this slice findable". */
  async function handleQuickSave(name: string): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.show('Pick a name for the view.', { kind: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await createBqView(sourceId, { name: trimmed, sql });
      toast.show(`Saved view "${trimmed}". Switch tabs above to come back to it.`, { kind: 'success' });
      setShowSaveSimple(false);
      setSaveName('');
      onViewSaved();
      // Switch the operator to the freshly-saved view so they see the
      // tab become active — confirms the save visually + lets them
      // immediately tweak from there.
      setTimeout(() => setBaseView(trimmed), 100);
    } catch (e) {
      toast.show(`Save failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  /** Delete a saved view with type-to-confirm guard. Handler at the
   *  panel level so the tabs row's hover ⋯ menu can reuse it. */
  async function handleDeleteView(name: string): Promise<void> {
    if (name === 'events') {
      toast.show("Can't delete the base 'events' view — it's auto-created from the source.", { kind: 'warning' });
      return;
    }
    const ok = await toast.confirm({
      title: 'Delete this view?',
      message: `${name} will be deleted from BigQuery. Triggers that scoped to it stop firing. Underlying events untouched.`,
      destructive: true,
      confirmLabel: 'Delete view',
    });
    if (!ok) return;
    try {
      await deleteBqView(sourceId, name);
      toast.show(`Deleted view "${name}".`, { kind: 'success' });
      if (baseView === name) {
        setBaseView('events');
      }
      onViewSaved();
    } catch (e) {
      toast.show(`Delete failed: ${(e as Error).message}`, { kind: 'error' });
    }
  }

  /** Duplicate a view — copy SQL into a new view with `_copy` suffix.
   *  Operator can rename right away from the tabs row. */
  async function handleDuplicateView(view: BqView): Promise<void> {
    const newName = `${view.name}_copy`;
    try {
      await createBqView(sourceId, { name: newName, sql: view.sql });
      toast.show(`Duplicated to "${newName}".`, { kind: 'success' });
      onViewSaved();
      setTimeout(() => setBaseView(newName), 100);
    } catch (e) {
      toast.show(`Duplicate failed: ${(e as Error).message}`, { kind: 'error' });
    }
  }

  /** Rename a view by create-then-delete. The BQ view URL contains the
   *  name so there's no PATCH-rename endpoint; we create a new view
   *  with the same SQL under the new name, then drop the old one. */
  async function handleRenameView(oldName: string, newName: string): Promise<void> {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) return;
    if (oldName === 'events') {
      toast.show("Can't rename the auto-created 'events' view.", { kind: 'warning' });
      return;
    }
    const view = views.find(v => v.name === oldName);
    if (!view) return;
    try {
      await createBqView(sourceId, { name: trimmed, sql: view.sql, description: view.description });
      await deleteBqView(sourceId, oldName);
      toast.show(`Renamed "${oldName}" → "${trimmed}".`, { kind: 'success' });
      onViewSaved();
      setTimeout(() => setBaseView(trimmed), 100);
    } catch (e) {
      toast.show(`Rename failed: ${(e as Error).message}`, { kind: 'error' });
    }
  }

  async function handleSave(): Promise<void> {
    if (!saveName.trim()) {
      toast.show('Pick a name for the new view.', { kind: 'warning' });
      return;
    }
    const triggerSpec = streamAction === 'none' ? null : buildTriggerAction();
    if (streamAction !== 'none' && !triggerSpec) return;
    setSubmitting(true);
    try {
      await createBqView(sourceId, { name: saveName.trim(), sql });
      if (triggerSpec) {
        let viewId: string | undefined;
        if (triggerSpec.needsViewScope) {
          const tree = filterRowsToTree(filters);
          if (tree) {
            const ftView = await createView(sourceId, { name: `${saveName.trim()}__filter`, filter: tree, columns: [] });
            viewId = ftView.view_id;
          }
        }
        await createTrigger(sourceId, {
          name: `${saveName.trim()}__${streamAction}`,
          action: triggerSpec.action,
          ...(viewId ? { view_id: viewId } : {}),
        });
        toast.show(`Created view "${saveName.trim()}" + ${streamAction} trigger.`, { kind: 'success' });
      } else {
        toast.show(`Created view "${saveName.trim()}".`, { kind: 'success' });
      }
      setShowSavePanel(false);
      setSaveName('');
      setStreamAction('none');
      clearRecipe();
      onViewSaved();
    } catch (e) {
      toast.show(`Save failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  const payloadFields = (schema ?? []).filter(f => f.path !== 'payload' && f.path !== 'origin');
  // Augment payload fields with system + origin + per-input-tag meta
  // paths so view filters can target them. The shapes mirror SchemaField
  // for compatibility with downstream consumers (chip rendering,
  // datalist autocomplete). Per-input-tag keys are pulled live from
  // source.inputs[*].tags so the dropdown surfaces exactly what's
  // stamped on the events that currently arrive.
  const metaFields = useMemo<SchemaField[]>(() => {
    const out: SchemaField[] = [
      // System (top-level BQ columns) — strings except derivation_depth.
      { path: 'received_at',       types: ['string'], sample_values: [], occurrences: 0 },
      { path: 'event_id',          types: ['string'], sample_values: [], occurrences: 0 },
      { path: 'event_key',         types: ['string'], sample_values: [], occurrences: 0 },
      { path: 'derivation_depth',  types: ['number'], sample_values: [], occurrences: 0 },
      { path: 'parent_event_id',   types: ['string'], sample_values: [], occurrences: 0 },
      // Origin meta-paths — populated for every event.
      { path: '$origin.kind',           types: ['string'], sample_values: ['input', 'trigger', 'reingest'], occurrences: 0 },
      { path: '$origin.id',             types: ['string'], sample_values: [], occurrences: 0 },
      { path: '$origin.input_id',       types: ['string'], sample_values: [], occurrences: 0 },
      { path: '$origin.input_name',     types: ['string'], sample_values: [], occurrences: 0 },
      { path: '$origin.source_filename',types: ['string'], sample_values: [], occurrences: 0 },
      { path: '$origin.upload_id',      types: ['string'], sample_values: [], occurrences: 0 },
      { path: '$origin.parse_format',   types: ['string'], sample_values: [], occurrences: 0 },
    ];
    // Per-input tag keys — dedup across all inputs on this source.
    const tagKeys = new Set<string>();
    for (const inp of source?.inputs ?? []) {
      for (const k of Object.keys(inp.tags ?? {})) tagKeys.add(k);
    }
    for (const k of [...tagKeys].sort()) {
      out.push({ path: `$origin.input_tags.${k}`, types: ['string'], sample_values: [], occurrences: 0 });
    }
    return out;
  }, [source]);
  const availableFields = useMemo<SchemaField[]>(() => [...payloadFields, ...metaFields], [payloadFields, metaFields]);
  // Drop columns prefixed with `_` — those are SQL-side metadata
  // (e.g. `_brand` from buildBrandSqlCase) that the table never
  // shows as cells. They surface elsewhere (brand chip in the row
  // gutter).
  // _fingerprint is the only `_`-prefixed column we DO surface in the
  // table — it's the schema-group badge for that row. Everything else
  // `_*` (e.g. `_brand`) is SQL-side metadata that renders inline
  // with other affordances (brand chip in the row gutter) rather than
  // as a column.
  const allTableColumns = previewRows && previewRows.length > 0
    ? [
        '_fingerprint',
        ...Object.keys(previewRows[0]!).filter(c => !c.startsWith('_') && c !== 'payload'),
      ]
    : [];
  const tableColumns = allTableColumns.filter(c => !hiddenColumns.has(c));

  // Apply fingerprint filter post-fetch — client-side narrowing so the
  // operator doesn't pay a new BQ query for each group click.
  const rowsToRender = useMemo<Array<Record<string, unknown>> | null>(() => {
    if (!previewRows) return null;
    if (!fingerprintFilter) return previewRows;
    return previewRows.filter(r => r._fingerprint === fingerprintFilter);
  }, [previewRows, fingerprintFilter]);
  // Type chip shown next to column name. Use schema-derived types for
  // payload columns; envelope columns hard-coded since they're not in
  // /events:schema. Returned chip is empty string for unknown so the
  // header doesn't render an empty rectangle.
  const columnType = (col: string): string => {
    if (col === 'event_id' || col === 'event_key' || col === 'parent_event_id'
      || col === 'source_id' || col === 'trace_id') return 'id';
    if (col === 'received_at') return 'time';
    if (col === 'derivation_depth') return 'int';
    if (col === 'payload' || col === 'origin') return 'json';
    const field = (schema ?? []).find(f => f.path === col);
    if (!field || field.types.length === 0) return '';
    // Schema returns multiple types when payload is heterogeneous; prefer
    // the most informative one (number > string > null) so the chip
    // doesn't read 'null' for a column with one stray null value.
    if (field.types.includes('number')) return 'num';
    if (field.types.includes('boolean')) return 'bool';
    if (field.types.includes('object')) return 'json';
    if (field.types.includes('array')) return 'array';
    if (field.types.includes('string')) return 'str';
    return field.types[0] ?? '';
  };

  /** Dispatcher for the Connected-Sheet top-toolbar actions. Each
   *  button maps to an existing affordance — we just route the click
   *  through one handler so the toolbar stays simple. */
  function handleToolbarAction(id: ToolbarAction): void {
    switch (id) {
      case 'pivot':
        setShowGroupByPicker(true);
        toast.show('Pivot — pick the column to group by below the toolbar.', { kind: 'info', ttlMs: 2500 });
        break;
      case 'extract':
        setSaveName(suggestViewName());
        setShowSaveSimple(true);
        break;
      case 'column_stats':
        toast.show('Right-click any column header → “📊 Show distribution” for top-50 values.', { kind: 'info', ttlMs: 3500 });
        break;
      case 'chart':
      case 'function':
      case 'calc_column':
      case 'advanced':
        toast.show('Coming soon. Pivot, Extract, and Column stats cover most operator needs today.', { kind: 'info', ttlMs: 3000 });
        break;
    }
  }

  // Full-screen wrapper styling. When fullscreen, the surface is
  // fixed-positioned over the rest of the page (TopHeader stays on
  // top via its own z-index). When normal, it lays out inline.
  const outerStyle: React.CSSProperties = fullscreen
    ? {
        display: 'flex', flexDirection: 'column', gap: 10,
        position: 'fixed', top: 56, left: 0, right: 0, bottom: 0,
        zIndex: 50,
        background: 'var(--bg)',
        padding: '12px 16px 16px',
        overflow: 'auto',
      }
    : { display: 'flex', flexDirection: 'column', gap: 12 };

  return (
    <div ref={surfaceRef} style={outerStyle}>
      {/* Google-Sheets-style "Connected Sheet" header — eyebrow naming
          where the data lives + Refresh button + Refresh options
          dropdown + Connection settings deep-link. Mirrors the row
          Google Sheets puts on top of a BigQuery-connected sheet. */}
      <ConnectedSheetHeader
        dataset={dataset}
        viewName={baseView}
        rowCount={previewRows?.length ?? null}
        lastRefreshAt={lastRefreshAt}
        refreshIntervalMs={refreshIntervalMs}
        loading={previewLoading}
        onRefresh={() => setRefreshKey(k => k + 1)}
        onChangeRefreshInterval={persistRefreshInterval}
      />

      {/* Action toolbar. Same icon set Google Sheets uses on Connected
          Sheets (Chart · Pivot · Function · Extract · Calculated col
          · Column stats · Advanced). Enabled ones wire to existing
          flows; disabled ones surface "Coming soon" on hover. */}
      <ConnectedSheetToolbar
        onAction={handleToolbarAction}
        fullscreen={fullscreen}
        onToggleFullscreen={() => setFullscreen(!fullscreen)}
      />

      {/* Tabs row — grouped by provenance.
            Group 1 ('events' base) is the raw stream — auto-created.
            Group 2 (auto-detected models) — descriptions start with
              'Auto-detected' from DetectedModelsPanel's createBqView.
            Group 3 (operator-curated) — everything else.
          Rendered with subtle group separators so the operator
          orients quickly when 20+ tabs accumulate. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 0, borderBottom: '1px solid var(--line)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', flex: 1, overflowX: 'auto' }}>
          {(() => {
            const baseEvents = views.filter(v => v.name === 'events');
            const autoDetected = views.filter(v => v.name !== 'events' && (v.description ?? '').startsWith('Auto-detected'));
            const myViews = views.filter(v => v.name !== 'events' && !(v.description ?? '').startsWith('Auto-detected'));
            const groups: Array<{ label: string; views: typeof views }> = [
              { label: '', views: baseEvents },
              { label: 'Auto-detected', views: autoDetected },
              { label: 'My views', views: myViews },
            ].filter(g => g.views.length > 0);
            return groups.flatMap((g, gi) => [
              ...(gi > 0 ? [
                <div
                  key={`sep-${gi}`}
                  style={{
                    display: 'flex', alignItems: 'flex-end',
                    padding: '0 10px 8px',
                    fontSize: 9.5, fontFamily: 'var(--mono)',
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    color: 'var(--ink-3)', whiteSpace: 'nowrap',
                    borderLeft: '1px dashed var(--line)',
                  }}
                >{g.label}</div>,
              ] : []),
              ...g.views.map(v => (
                <ViewTab
                  key={v.name}
                  view={v}
                  active={baseView === v.name}
                  dataset={dataset}
                  onActivate={() => { setBaseView(v.name); clearRecipe(); }}
                  onRename={(next) => { void handleRenameView(v.name, next); }}
                  onDuplicate={() => { void handleDuplicateView(v); }}
                  onDelete={() => { void handleDeleteView(v.name); }}
                  onCopySql={() => { void navigator.clipboard.writeText(v.sql ?? ''); toast.show(`SQL for "${v.name}" copied`, { kind: 'success' }); }}
                />
              )),
            ]);
          })()}
          {/* + View — always visible primary CTA. The impatient operator
              should never have to wonder where to save their slice. We
              pre-fill the name suggestion at the moment of click so it
              reflects the active filter state. */}
          <button
            type="button"
            onClick={() => { setSaveName(suggestViewName()); setShowSaveSimple(true); }}
            title="Save the current slice (filters, search, sort, hidden columns) as a named view tab"
            style={{
              padding: '8px 14px', fontSize: 12.5, fontFamily: 'var(--mono)',
              background: 'transparent',
              color: 'var(--accent)',
              border: 'none',
              borderTop: '2px solid transparent',
              borderLeft: '1px dashed var(--line)',
              marginBottom: -1,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >+ View</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', marginLeft: 8, marginBottom: 4 }}>
          <button
            type="button"
            onClick={() => setRefreshKey(k => k + 1)}
            disabled={previewLoading}
            title={`Refresh — re-query the view. ${refreshIntervalMs > 0 ? `Auto-refresh every ${refreshIntervalMs / 1000}s.` : 'Manual refresh only.'}${lastRefreshAt ? `\nLast refreshed ${Math.floor((Date.now() - lastRefreshAt) / 1000)}s ago.` : ''}`}
            aria-label="Refresh view"
            style={{
              padding: '6px 10px',
              background: 'transparent', border: '1px solid var(--line)', borderRadius: 4,
              color: previewLoading ? 'var(--ink-3)' : 'var(--ink-2)',
              cursor: previewLoading ? 'wait' : 'pointer',
              fontSize: 14, lineHeight: 1,
            }}
          >
            <span style={{
              display: 'inline-block',
              transform: previewLoading ? 'rotate(180deg)' : 'none',
              transition: previewLoading ? 'transform 0.6s linear' : 'transform 0.4s ease',
              animation: previewLoading ? 'v3-spin 0.9s linear infinite' : 'none',
            }}>↻</span>
          </button>
          {/* Subtle countdown bar under the refresh button — fills 0→100%
              over the auto-refresh interval, then snaps back when a new
              fetch completes. Color shifts amber when polling is paused
              because the operator is interacting (hover / focus / scroll
              away from top); tells them "polling stopped, your work is
              safe — click ↻ when you want fresh data". */}
          <div style={{
            height: 2, marginTop: 2, background: 'var(--bg-sunken)', borderRadius: 1, overflow: 'hidden',
          }}>
            <div
              title={isInteracting
                ? 'Auto-refresh paused — pointer over table, input focused, or scrolled. Click ↻ to refresh now.'
                : refreshIntervalMs > 0 ? `Auto-refresh every ${refreshIntervalMs / 1000}s` : 'Manual refresh only — click ↻ when you want fresh data.'}
              style={{
                height: '100%',
                width: `${(isInteracting ? 1 : refreshProgress) * 100}%`,
                background: isInteracting
                  ? 'oklch(0.78 0.10 75 / 0.7)'
                  : 'oklch(0.62 0.18 145 / 0.6)',
                transition: isInteracting ? 'background 0.3s ease' : 'width 1s linear, background 0.3s ease',
              }}
            />
          </div>
        </div>
        <style>{`
          @keyframes v3-spin {
            from { transform: rotate(0deg) }
            to   { transform: rotate(360deg) }
          }
        `}</style>
      </div>

      {/* Recipe toolbar — search, filter, date scope, fields, save.
          Date scope is a compact dropdown ('All time' default) so the
          chips bar (which used 4 always-visible buttons) doesn't
          dominate the toolbar when the operator isn't filtering by
          time — which is most of the time. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder="🔎 search payload"
          title="Free-text search across payload — case-insensitive LIKE on TO_JSON_STRING(payload)"
          style={{
            ...inputStyle,
            width: 220, padding: '4px 10px',
            fontSize: 12, fontFamily: 'var(--ui)',
          }}
        />
        {searchText && (
          <button
            type="button"
            onClick={() => setSearchText('')}
            className="btn sm ghost"
            style={{ padding: '4px 8px', fontSize: 11, marginLeft: -4 }}
            title="Clear search"
          >×</button>
        )}
        <select
          value={dateScope}
          onChange={e => setDateScope(e.target.value as DateScope)}
          title="Time window for the table — applies a received_at filter at the base table level"
          style={{
            ...inputStyle, padding: '4px 8px', fontFamily: 'var(--ui)', fontSize: 11.5,
            background: dateScope === 'all' ? 'var(--bg)' : 'var(--accent-soft)',
            color: dateScope === 'all' ? 'var(--ink-2)' : 'var(--accent)',
            borderColor: dateScope === 'all' ? 'var(--line)' : 'var(--accent-line)',
            cursor: 'pointer',
          }}
        >
          <option value="all">📅 All time</option>
          <option value="hour">📅 Last hour</option>
          <option value="today">📅 Today</option>
          <option value="7d">📅 Last 7 days</option>
        </select>
        {(() => {
          // Group source.inputs by kind so the dropdown can list the
          // named inputs underneath "All webhooks" / "All file
          // uploads" / etc. — only when there are 2+ of a kind (one
          // single webhook doesn't need a sub-option; the kind-level
          // "All webhooks" already matches it).
          const ORIGIN_KIND_META: Array<{ kind: SourceInput['kind']; filter: OriginFilter; emoji: string; label: string }> = [
            { kind: 'webhook',     filter: 'webhook',  emoji: '🌐', label: 'Webhook' },
            { kind: 'file_upload', filter: 'upload',   emoji: '📁', label: 'File upload' },
            { kind: 'api_pull',    filter: 'api_pull', emoji: '🔄', label: 'API pull' },
            { kind: 'email',       filter: 'email',    emoji: '📧', label: 'Email' },
            { kind: 'manual',      filter: 'manual',   emoji: '🧪', label: 'Manual test' },
          ];
          const inputsByKind = new Map<SourceInput['kind'], SourceInput[]>();
          for (const inp of source?.inputs ?? []) {
            if (inp.status === 'archived') continue;
            const arr = inputsByKind.get(inp.kind) ?? [];
            arr.push(inp);
            inputsByKind.set(inp.kind, arr);
          }
          return (
            <select
              value={originKind}
              onChange={e => setOriginKind(e.target.value as OriginFilter)}
              title="Filter by how the event arrived. With 2+ inputs of the same kind, pick a specific one by name."
              style={{
                ...inputStyle, padding: '4px 8px', fontFamily: 'var(--ui)', fontSize: 11.5,
                background: originKind === 'all' ? 'var(--bg)' : 'var(--accent-soft)',
                color: originKind === 'all' ? 'var(--ink-2)' : 'var(--accent)',
                borderColor: originKind === 'all' ? 'var(--line)' : 'var(--accent-line)',
                cursor: 'pointer',
                maxWidth: 260,
              }}
            >
              <option value="all">📥 Any input</option>
              {ORIGIN_KIND_META.map(meta => {
                const inputs = inputsByKind.get(meta.kind) ?? [];
                if (inputs.length === 0) return null;
                // Single input of this kind → render ONE option in the
                // shape "<emoji> <Type> — <Name>" (drilled straight to
                // that input by id; no kind-level "All <type>" option
                // because it would match the same single row).
                if (inputs.length === 1) {
                  const inp = inputs[0]!;
                  const name = inp.name?.trim() || inp.input_id.slice(-6);
                  return (
                    <option key={meta.kind} value={`input:${inp.input_id}`}>
                      {meta.emoji} {meta.label} — {name}
                      {inp.status === 'paused' ? ' · paused' : ''}
                    </option>
                  );
                }
                // Multiple inputs of this kind → optgroup header with the
                // type, "All <type>s" master option, then one named row
                // per input.
                return (
                  <optgroup key={meta.kind} label={`${meta.emoji} ${meta.label} (${inputs.length})`}>
                    <option value={meta.filter}>{meta.emoji} All {meta.label.toLowerCase()}s</option>
                    {inputs.map(inp => (
                      <option key={inp.input_id} value={`input:${inp.input_id}`}>
                        ↳ {inp.name?.trim() || `${meta.label} ${inp.input_id.slice(-6)}`}
                        {inp.status === 'paused' ? ' · paused' : ''}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          );
        })()}
        {brandFilter && (
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 4px 3px 10px', fontSize: 11.5, fontFamily: 'var(--mono)',
              background: 'var(--accent-soft)', border: '1px solid var(--accent-line)',
              color: 'var(--accent)', borderRadius: 14,
            }}
            title={`Filtered to brand: ${BRAND_BY_ID[brandFilter]?.label ?? brandFilter}`}
          >
            {BRAND_BY_ID[brandFilter]?.emoji ?? '🏷'} {BRAND_BY_ID[brandFilter]?.label ?? brandFilter}
            <button
              type="button"
              onClick={() => setBrandFilter(null)}
              title="Clear brand filter"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--accent)', fontSize: 13, padding: '0 6px', lineHeight: 1,
              }}
            >×</button>
          </span>
        )}
        <button type="button" onClick={addFilter} className="btn sm ghost" style={{ padding: '4px 10px', fontSize: 11.5 }}>+ filter</button>
        {groupByField ? (
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 4px 3px 10px', fontSize: 11.5, fontFamily: 'var(--mono)',
              background: 'var(--accent-soft)', border: '1px solid var(--accent-line)',
              color: 'var(--accent)', borderRadius: 14,
            }}
            title="Pivot mode active — table shows counts per unique value of this field"
          >
            📊 Grouped by <code>{groupByField}</code>
            <button
              type="button"
              onClick={() => setGroupByField(null)}
              title="Exit pivot mode — back to row view"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--accent)', fontSize: 13, padding: '0 6px', lineHeight: 1,
              }}
            >×</button>
          </span>
        ) : (
          <div ref={groupByPickerRef} style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setShowGroupByPicker(o => !o)}
              className="btn sm ghost"
              style={{ padding: '4px 10px', fontSize: 11.5 }}
              title="Pivot table by a field — see counts per unique value (replaces row view)"
            >📊 Group by</button>
            {showGroupByPicker && (
              <div style={{
                position: 'absolute', left: 0, top: '100%', marginTop: 4, zIndex: 30,
                background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 6,
                boxShadow: '0 6px 20px oklch(0.18 0.01 250 / 0.20)',
                minWidth: 220, maxHeight: 320, overflowY: 'auto',
                padding: 4, display: 'flex', flexDirection: 'column', gap: 1,
              }}>
                <div style={{
                  padding: '4px 10px', fontSize: 10.5, fontFamily: 'var(--mono)',
                  textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)',
                  borderBottom: '1px solid var(--line)', marginBottom: 4,
                }}>Group by field</div>
                {availableFields.length === 0 && (
                  <div style={{ padding: '8px 10px', fontSize: 11.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                    No fields detected yet. Send events first.
                  </div>
                )}
                {availableFields.slice(0, 30).map(f => (
                  <button
                    key={f.path}
                    type="button"
                    onClick={() => { setGroupByField(f.path); setShowGroupByPicker(false); }}
                    style={{
                      textAlign: 'left', padding: '5px 10px',
                      fontFamily: 'var(--mono)', fontSize: 11.5,
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: 'var(--ink)', borderRadius: 3,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-sunken)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.path}</span>
                    <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>{f.types.join('|')}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {hasRecipeEdits && (
          <button type="button" onClick={clearRecipe} className="btn sm ghost" style={{ padding: '4px 10px', fontSize: 11.5, color: 'var(--ink-3)' }}>clear recipe</button>
        )}
        {previewRows && previewRows.length > 0 && (
          <button
            type="button"
            onClick={() => exportPreview(previewRows, baseView, 'csv', toast)}
            className="btn sm ghost"
            style={{ padding: '4px 10px', fontSize: 11.5 }}
            title="Download the visible rows as CSV — bounded by the preview limit (currently 50)"
          >↓ CSV</button>
        )}
        {previewRows && previewRows.length > 0 && (
          <button
            type="button"
            onClick={() => exportPreview(previewRows, baseView, 'json', toast)}
            className="btn sm ghost"
            style={{ padding: '4px 10px', fontSize: 11.5 }}
            title="Download the visible rows as NDJSON"
          >↓ JSON</button>
        )}
        {previewRows && previewRows.length > 0 && (
          <button
            type="button"
            onClick={() => copyPreviewAsMarkdown(previewRows, toast)}
            className="btn sm ghost"
            style={{ padding: '4px 10px', fontSize: 11.5 }}
            title="Copy the visible rows as a Markdown table — paste into Slack / Notion / GitHub"
          >📋 MD</button>
        )}
        {baseView === 'events' && !hasRecipeEdits && (
          <label
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 11.5, color: 'var(--ink-3)', fontFamily: 'var(--mono)',
              cursor: 'pointer',
            }}
            title={showSystemFields
              ? 'Showing all envelope columns (event_id, source_id, event_key, payload, origin, …)'
              : 'Show event_id, source_id, event_key, payload (raw JSON), origin, derivation_depth, parent_event_id, trace_id'}
          >
            <input
              type="checkbox"
              checked={showSystemFields}
              onChange={e => setShowSystemFields(e.target.checked)}
              style={{ margin: 0 }}
            />
            system fields
          </label>
        )}
        <label
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 11.5, color: 'var(--ink-3)', fontFamily: 'var(--mono)',
            cursor: 'pointer',
          }}
          title="Show the payload field sidebar (occurrence counts, type chips, click to add is-present filter)"
        >
          <input
            type="checkbox"
            checked={showSchemaSidebar}
            onChange={e => setShowSchemaSidebar(e.target.checked)}
            style={{ margin: 0 }}
          />
          schema panel
        </label>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => setShowShortcuts(s => !s)}
          className="btn sm ghost"
          style={{ padding: '4px 8px', fontSize: 11.5 }}
          title="Keyboard shortcuts (press ?)"
          aria-label="Show keyboard shortcuts"
        >?</button>
        {/* No second Save CTA here — the always-visible '+ View'
            button next to the tabs is the canonical save path.
            Two CTAs for the same action confused operators. */}
      </div>

      {/* Drilled-from-pivot chip — extracted to its own component. */}
      <DrillbackChip
        state={drillback}
        setFilters={setFilters}
        setGroupByField={setGroupByField}
        setDrillback={setDrillback}
      />

      {/* Active fingerprint filter banner — shows the operator they're
          viewing a narrowed slice of structural groups. ESC clears, ×
          clears, click anywhere outside the table doesn't (the filter
          is intentionally sticky until cleared). */}
      {fingerprintFilter && (() => {
        const hue = parseInt(fingerprintFilter.slice(4, 7), 16) % 360;
        const visibleCount = rowsToRender?.length ?? 0;
        const totalCount = previewRows?.length ?? 0;
        const { label, isNamed } = resolveFingerprintLabel(fingerprintFilter, schemaByFingerprint);
        return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 10px',
            background: `oklch(0.96 0.04 ${hue} / 0.5)`,
            border: `1px solid oklch(0.82 0.10 ${hue})`,
            borderRadius: 6, fontSize: 11.5,
          }}>
            <span style={{ color: `oklch(0.36 0.14 ${hue})` }}>
              {isNamed ? 'Filtered to schema ' : 'Filtered to auto-named group '}
              <code style={{ fontFamily: 'var(--mono)', fontWeight: isNamed ? 600 : 400 }}>{label}</code>
              {' · '}{visibleCount} of {totalCount} rows
            </span>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={() => setFingerprintFilter(null)}
              title="Clear group filter (Esc)"
              aria-label="Clear group filter"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--ink-3)', fontSize: 13, padding: '0 6px', lineHeight: 1,
              }}
            >×</button>
          </div>
        );
      })()}

      {/* Filter rail compact mode — extracted to its own component.
          Full-editor branch below stays inline (richer surface). */}
      {filtersCompact && (
        <FilterRailCompact
          filters={filters}
          uniqueByPath={uniqueByPath}
          setFiltersExpanded={setFiltersExpanded}
          removeFilter={removeFilter}
        />
      )}
      {filters.length > 0 && !filtersCompact && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '8px 10px', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 6 }}>
          {filters.map((f, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)', minWidth: 32 }}>{idx === 0 ? 'WHERE' : 'AND'}</span>
              <input
                value={f.field_path}
                onChange={e => updateFilter(idx, { field_path: e.target.value })}
                placeholder="field name (e.g. status, customer.email)"
                style={{ ...inputStyle, flex: 1 }}
                list={`schema-${idx}`}
              />
              <datalist id={`schema-${idx}`}>{availableFields.map(s => <option key={s.path} value={s.path} />)}</datalist>
              <select value={f.op} onChange={e => updateFilter(idx, { op: e.target.value as FilterRow['op'] })} style={inputStyle}>
                <option value="is_present">is present</option>
                <option value="is_empty">is empty</option>
                <option value="eq">=</option>
                <option value="neq">!=</option>
                <option value="gt">&gt;</option>
                <option value="lt">&lt;</option>
                <option value="contains">contains</option>
              </select>
              {!['is_present', 'is_empty'].includes(f.op) && (
                <input value={f.value} onChange={e => updateFilter(idx, { value: e.target.value })} placeholder="value" style={{ ...inputStyle, flex: 1 }} />
              )}
              <button type="button" onClick={() => removeFilter(idx)} style={{ background: 'transparent', border: '1px solid var(--line)', borderRadius: 4, padding: '5px 8px', cursor: 'pointer', color: 'var(--ink-3)', fontFamily: 'var(--mono)', fontSize: 12 }}>×</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)', minWidth: 96 }}>UNIQUE BY</span>
            <input value={uniqueByPath} onChange={e => setUniqueByPath(e.target.value)} placeholder="(optional) field to keep only the latest entry per — e.g. order_id" style={{ ...inputStyle, flex: 1 }} list="unique-schema" />
            <datalist id="unique-schema">{availableFields.map(s => <option key={s.path} value={s.path} />)}</datalist>
            <select value={uniqueOrderBy} onChange={e => setUniqueOrderBy(e.target.value as 'received_at_desc' | 'received_at_asc')} style={inputStyle} disabled={!uniqueByPath.trim()}>
              <option value="received_at_desc">last wins</option>
              <option value="received_at_asc">first wins</option>
            </select>
            {filters.length >= 3 && (
              <button
                type="button"
                onClick={() => setFiltersExpanded(false)}
                className="btn sm ghost"
                style={{ padding: '4px 10px', fontSize: 11 }}
                title="Collapse the editor back to chip view"
              >collapse</button>
            )}
          </div>
        </div>
      )}

      {/* Volume histogram — extracted to its own component. Parent
          guards on baseView === 'events' to keep it off non-events
          tabs; the component itself guards on empty rows. */}
      {baseView === 'events' && (
        <VolumeHistogram rows={histogramRows} onClickBar={() => setDateScope('hour')} />
      )}

      {/* Body: table (full width by default) + optional schema sidebar
          when the operator opts in. Sidebar is collapsed by default —
          the auto-projected payload columns ARE the schema in V3, so a
          fixed left rail mostly steals horizontal real estate. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: showSchemaSidebar ? 'minmax(0, 240px) minmax(0, 1fr)' : 'minmax(0, 1fr)',
        gap: 12,
        alignItems: 'start',
      }}>
        {showSchemaSidebar && (
          <SchemaSidebar
            fields={availableFields}
            activeFilters={filters}
            onToggleFilter={toggleSchemaFilter}
          />
        )}

        <div style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 6, overflow: 'hidden' }}>
          {selectedEventIds.size > 0 && (
            <div style={{
              padding: '6px 10px',
              display: 'flex', gap: 8, alignItems: 'center',
              background: 'var(--accent-soft)',
              borderBottom: '1px solid var(--accent-line)',
              fontSize: 11.5, color: 'var(--accent)',
            }}>
              <strong style={{ fontFamily: 'var(--mono)' }}>{selectedEventIds.size} selected</strong>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                className="btn sm ghost"
                onClick={() => {
                  void navigator.clipboard.writeText([...selectedEventIds].join('\n'));
                  toast.show(`Copied ${selectedEventIds.size} event_id${selectedEventIds.size === 1 ? '' : 's'}`, { kind: 'success' });
                }}
                style={{ padding: '2px 10px', fontSize: 11 }}
                title="Copy selected event_ids to clipboard, one per line"
              >📋 IDs</button>
              <button
                type="button"
                className="btn sm ghost"
                onClick={() => {
                  const subset = (previewRows ?? []).filter(r => selectedEventIds.has(String(r.event_id ?? '')));
                  exportPreview(subset, `${baseView}_selected`, 'csv', toast);
                }}
                style={{ padding: '2px 10px', fontSize: 11 }}
                title="Download the selected rows as CSV"
              >↓ CSV</button>
              <button
                type="button"
                className="btn sm ghost"
                onClick={() => {
                  const subset = (previewRows ?? []).filter(r => selectedEventIds.has(String(r.event_id ?? '')));
                  exportPreview(subset, `${baseView}_selected`, 'json', toast);
                }}
                style={{ padding: '2px 10px', fontSize: 11 }}
                title="Download the selected rows as NDJSON"
              >↓ JSON</button>
              <button
                type="button"
                className="btn sm ghost"
                onClick={() => {
                  const subset = (previewRows ?? []).filter(r => selectedEventIds.has(String(r.event_id ?? '')));
                  copyPreviewAsMarkdown(subset, toast);
                }}
                style={{ padding: '2px 10px', fontSize: 11 }}
                title="Copy the selected rows as a Markdown table"
              >📋 MD</button>
              <button
                type="button"
                className="btn sm ghost"
                onClick={() => setSelectedEventIds(new Set())}
                style={{ padding: '2px 10px', fontSize: 11 }}
                title="Clear selection"
              >clear</button>
            </div>
          )}
          <div style={{ padding: '6px 10px', display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid var(--line)', background: 'var(--bg-sunken)', fontSize: 11, color: 'var(--ink-3)' }}>
            <code style={{ fontFamily: 'var(--mono)' }}>{previewLoading ? 'refreshing…' : previewRows ? `${previewRows.length} row${previewRows.length === 1 ? '' : 's'}` : ''}</code>
            {lastRefreshAt && !previewLoading && (() => {
              const ms = Date.now() - lastRefreshAt;
              const stale = refreshIntervalMs > 0 && ms > refreshIntervalMs * 3;
              const ago = ms < 1000 ? 'just now' : ms < 60_000 ? `${Math.floor(ms / 1000)}s ago` : `${Math.floor(ms / 60_000)}m ago`;
              return (
                <span
                  title={stale ? 'Polling has fallen behind — likely the tab was backgrounded. Press \`r\` or click ↻ to refresh now.' : `Last successful refresh ${ago}`}
                  style={{
                    fontFamily: 'var(--mono)',
                    color: stale ? 'oklch(0.62 0.18 60)' : 'var(--ink-3)',
                    fontSize: 10.5,
                  }}
                >· {stale ? `stale ${ago}` : ago}</span>
              );
            })()}
            {(() => {
              const s = queryStatsRef.current;
              if (s.count === 0) return null;
              const avgMs = Math.round(s.total / s.count);
              // Color bands: <300ms = subtle, 300-1500ms = readable,
              // 1500-3000ms = ink, >3000ms = amber. Operator can
              // glance and tell when a query is slowing down.
              const color = s.last > 3000 ? 'oklch(0.62 0.18 60)'
                : s.last > 1500 ? 'var(--ink)'
                : s.last > 300 ? 'var(--ink-2)'
                : 'var(--ink-3)';
              const label = s.last < 1000 ? `${s.last}ms` : `${(s.last / 1000).toFixed(2)}s`;
              return (
                <span
                  title={`BigQuery round-trip (last query)\n\nLast: ${s.last}ms\nAvg over ${s.count} queries: ${avgMs}ms\nRange: ${s.min === Infinity ? 0 : s.min}ms – ${s.max}ms\n\nIncludes ~200ms client debounce + network.`}
                  style={{
                    fontFamily: 'var(--mono)',
                    color,
                    fontSize: 10.5,
                  }}
                >· ⏱ {label}{s.count > 1 ? ` (avg ${avgMs < 1000 ? avgMs + 'ms' : (avgMs / 1000).toFixed(2) + 's'})` : ''}</span>
              );
            })()}
            {lastJobStats && !previewLoading && (() => {
              // BQ JobStatistics chip — bytes processed (cost
              // dimension), slot ms, cache hit. Operator can see
              // 'cached · 0 B' on a re-run, '12.4 MB · 230ms slot'
              // on a fresh scan. KPI #9.
              const bytes = lastJobStats.bytes_processed;
              const cacheHit = lastJobStats.cache_hit;
              const fmt = (n: number): string =>
                n < 1024 ? `${n} B`
                : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB`
                : n < 1024 * 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB`
                : `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
              const cost = bytes / (1024 ** 4) * 5; // BQ on-demand $5/TB
              return (
                <span
                  title={`BigQuery scan stats — what you'll be billed on\n\nBytes processed: ${bytes.toLocaleString()} (${fmt(bytes)})\nCache hit: ${cacheHit ? 'yes (free)' : 'no'}\nSlot time: ${lastJobStats.slot_ms} ms\nEstimated cost: $${cost.toFixed(6)} at on-demand $5/TB`}
                  style={{
                    fontFamily: 'var(--mono)',
                    color: cacheHit ? 'oklch(0.42 0.16 145)' : bytes > 1024 * 1024 * 100 ? 'oklch(0.62 0.18 60)' : 'var(--ink-3)',
                    fontSize: 10.5,
                  }}
                >· {cacheHit ? '⚡ cached' : `📊 ${fmt(bytes)}`}</span>
              );
            })()}
            {previewError && <span style={{ color: 'var(--danger)' }}>· {previewError.slice(0, 100)}</span>}
            <span style={{ flex: 1 }} />
            {allTableColumns.length > 0 && (
              <button
                type="button"
                onClick={openFieldsDrawer}
                style={{
                  fontFamily: 'var(--mono)', fontSize: 11,
                  background: hiddenColumns.size > 0 ? 'var(--accent-soft)' : 'transparent',
                  border: `1px solid ${hiddenColumns.size > 0 ? 'var(--accent-line)' : 'var(--line)'}`,
                  color: hiddenColumns.size > 0 ? 'var(--accent)' : 'var(--ink-3)',
                  cursor: 'pointer', borderRadius: 3,
                  padding: '2px 8px',
                }}
                title={hiddenColumns.size > 0 ? `${hiddenColumns.size} field${hiddenColumns.size === 1 ? '' : 's'} hidden — click to open Select Fields` : 'Select fields to show'}
              >fields {hiddenColumns.size > 0 ? `(${allTableColumns.length - hiddenColumns.size}/${allTableColumns.length})` : ''}</button>
            )}
            <button
              type="button"
              onClick={() => { void navigator.clipboard.writeText(sql); toast.show('SQL copied', { kind: 'success' }); }}
              title={`SQL: ${sql}\n\nClick to copy.`}
              style={{
                fontFamily: 'var(--mono)', fontSize: 11,
                background: 'transparent', border: 'none',
                color: 'var(--ink-3)', cursor: 'pointer',
                padding: '0 4px',
                maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >📋 {sql.length > 80 ? sql.slice(0, 77) + '…' : sql}</button>
          </div>
          {previewRows && previewRows.length > 0 && (
            <div ref={tableScrollRef} style={{ overflow: 'auto', maxHeight: 540 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--mono)', fontSize: 11 }}>
                <thead style={{ background: 'var(--bg-sunken)', position: 'sticky', top: 0 }}>
                  <tr>
                    {/* Brand chip column — always present in pivot/non-pivot
                        events view. Empty header to keep gutter slim. */}
                    {!groupByField && previewRows && previewRows[0] && '_brand' in previewRows[0] && (
                      <th
                        scope="col"
                        aria-label="Service brand"
                        style={{
                          padding: '6px 8px', borderBottom: '1px solid var(--line)',
                          width: 36, textAlign: 'center',
                        }}
                        title="Auto-detected service brand per event (Slack, Stripe, Linear, …) — click any chip to filter to that brand"
                      >🏷</th>
                    )}
                    {/* Selection column header — checkbox toggles all-on-page. */}
                    {hasEventIdColumn(tableColumns) && (
                      <th style={{
                        padding: '6px 8px', borderBottom: '1px solid var(--line)',
                        width: 32, textAlign: 'center',
                      }}>
                        <input
                          type="checkbox"
                          checked={previewRows.length > 0 && previewRows.every(r => selectedEventIds.has(String(r.event_id ?? '')))}
                          ref={el => {
                            if (!el) return;
                            const someSelected = previewRows.some(r => selectedEventIds.has(String(r.event_id ?? '')));
                            const allSelected = previewRows.every(r => selectedEventIds.has(String(r.event_id ?? '')));
                            el.indeterminate = someSelected && !allSelected;
                          }}
                          onChange={() => toggleAllSelected()}
                          title="Toggle all rows on this page"
                          style={{ cursor: 'pointer', margin: 0 }}
                        />
                      </th>
                    )}
                    {tableColumns.map(c => {
                    // The fingerprint column gets a friendlier label
                    // + a tooltip explaining the affordance. No sort
                    // (fingerprints are categorical), no right-click
                    // menu (no per-fingerprint filter ops yet).
                    if (c === '_fingerprint') {
                      return (
                        <th
                          key={c}
                          title="Schema group of each row — same value means same structural shape. Click any cell to filter to that group."
                          style={{
                            padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid var(--line)',
                            fontWeight: 500, color: 'var(--ink-2)', whiteSpace: 'nowrap',
                            userSelect: 'none',
                          }}
                        >group</th>
                      );
                    }
                    const isSorted = sortColumn === c;
                    const sortIcon = isSorted ? (sortDir === 'ASC' ? ' ↑' : ' ↓') : '';
                    const typeLabel = columnType(c);
                    const headerNumeric = typeLabel === 'num' || typeLabel === 'int';
                    return (
                      <th
                        key={c}
                        onClick={() => cycleSort(c)}
                        onContextMenu={e => {
                          e.preventDefault();
                          setHeaderMenu({ col: c, left: e.clientX, top: e.clientY });
                        }}
                        title={`Click to sort · right-click for menu${typeLabel ? ` · type: ${typeLabel}` : ''}`}
                        style={{
                          padding: '6px 10px', textAlign: headerNumeric ? 'right' : 'left', borderBottom: '1px solid var(--line)',
                          fontWeight: 500, color: isSorted ? 'var(--accent)' : 'var(--ink-2)', whiteSpace: 'nowrap',
                          cursor: 'pointer', userSelect: 'none',
                        }}
                      >{c}
                        {typeLabel && (
                          <span style={{
                            marginLeft: 6, padding: '0 5px',
                            fontFamily: 'var(--mono)', fontSize: 9,
                            background: 'var(--bg)', border: '1px solid var(--line)',
                            borderRadius: 2, color: 'var(--ink-3)',
                            textTransform: 'lowercase', verticalAlign: 'middle',
                          }}>{typeLabel}</span>
                        )}
                        {sortIcon}
                      </th>
                    );
                  })}
                  {/* B3 drill-in: pinned-path columns persisted in
                      localStorage. Each header has an "x" to unpin. */}
                  {pinnedPaths.map(p => (
                    <th
                      key={`pin:${p}`}
                      title={`Pinned nested path · click × to unpin`}
                      style={{
                        padding: '6px 10px', textAlign: 'left',
                        borderBottom: '1px solid var(--line)',
                        background: 'oklch(0.96 0.04 235 / 0.5)',
                        fontWeight: 500, whiteSpace: 'nowrap', userSelect: 'none',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <code style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)' }}>{p}</code>
                        <button
                          type="button"
                          onClick={() => togglePinnedPath(p)}
                          aria-label={`Unpin ${p}`}
                          title={`Unpin ${p}`}
                          style={{
                            padding: '0 5px', fontSize: 11, lineHeight: 1,
                            background: 'transparent', border: '1px solid var(--line)',
                            borderRadius: 3, color: 'var(--ink-3)', cursor: 'pointer',
                          }}
                        >×</button>
                      </span>
                    </th>
                  ))}
                  </tr>
                </thead>
                <tbody>
                  {(rowsToRender ?? []).map((row, i) => {
                    const eventId = typeof row.event_id === 'string' ? row.event_id : '';
                    const isNew = eventId && newEventIds.has(eventId);
                    const isSelected = eventId && selectedEventIds.has(eventId);
                    const brandId = typeof row._brand === 'string' ? row._brand : null;
                    const brand = brandId ? BRAND_BY_ID[brandId] : null;
                    return (
                      <tr
                        key={eventId || i}
                        onMouseEnter={() => eventId && setHoverRowId(eventId)}
                        onMouseLeave={() => setHoverRowId(prev => prev === eventId ? null : prev)}
                        style={{
                          borderBottom: '1px solid var(--line)',
                          background: isSelected ? 'var(--accent-soft)' : isNew ? 'oklch(0.96 0.10 145)' : 'transparent',
                          transition: 'background 1.5s ease',
                        }}
                      >
                        {!groupByField && '_brand' in row && (
                          <td style={{ padding: '6px 4px', textAlign: 'center', width: 36 }}>
                            {brand ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBrandFilter(brandId);
                                  toast.show(`Filtered to ${brand.emoji} ${brand.label}`, { kind: 'success' });
                                }}
                                title={`Detected as ${brand.label} — click to filter rows to this brand only.\nSignature: ${brand.signature.join(' + ')}`}
                                aria-label={`Detected as ${brand.label} — click to filter to ${brand.label} only`}
                                style={{
                                  background: 'transparent', border: 'none', cursor: 'pointer',
                                  fontSize: 14, padding: '2px 4px', lineHeight: 1,
                                }}
                              >{brand.emoji}</button>
                            ) : (
                              <span
                                aria-label="No service brand detected"
                                style={{ color: 'var(--ink-3)', fontSize: 11 }}
                                title="No brand recognised"
                              >—</span>
                            )}
                          </td>
                        )}
                        {hasEventIdColumn(tableColumns) && (
                          <td style={{ padding: '6px 8px', textAlign: 'center', width: 32 }}>
                            <input
                              type="checkbox"
                              checked={!!isSelected}
                              onChange={() => eventId && toggleRowSelected(eventId)}
                              disabled={!eventId}
                              title={eventId ? 'Toggle selection' : 'No event_id on this row'}
                              style={{ cursor: eventId ? 'pointer' : 'not-allowed', margin: 0 }}
                              onClick={e => e.stopPropagation()}
                            />
                          </td>
                        )}
                        {tableColumns.map(c => {
                          // Special-case the schema-group badge: deterministic
                          // colour from a hash of the value so same-shape rows
                          // visually align. Click toggles the in-memory
                          // fingerprint filter — operator sees only that
                          // group's rows. Active filter row highlights with a
                          // ring; click again clears.
                          if (c === '_fingerprint') {
                            const fp = typeof row._fingerprint === 'string' ? row._fingerprint : '';
                            if (!fp) {
                              return (
                                <td key={c} style={{ padding: '6px 10px', color: 'var(--ink-3)', fontFamily: 'var(--mono)', fontSize: 11 }}>—</td>
                              );
                            }
                            // Stable colour per fingerprint hash (12 hex chars).
                            const hue = parseInt(fp.slice(4, 7), 16) % 360;
                            const active = fingerprintFilter === fp;
                            const { label, isNamed } = resolveFingerprintLabel(fp, schemaByFingerprint);
                            return (
                              <td key={c} style={{ padding: '6px 10px' }}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFingerprintFilter(active ? null : fp);
                                  }}
                                  title={active
                                    ? `Active filter — only rows in this group. Click to clear.\n${isNamed ? 'Schema: ' : 'Auto-named: '}${label}\nFingerprint: ${fp}`
                                    : `Show only rows in this structural group. Click again to clear.\n${isNamed ? 'Schema: ' : 'Auto-named: '}${label}\nFingerprint: ${fp}`}
                                  aria-label={active ? `Clear filter for ${label}` : `Filter to ${label}`}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    padding: '1px 6px',
                                    fontFamily: 'var(--mono)', fontSize: 10.5,
                                    fontWeight: isNamed ? 600 : 400,
                                    background: `oklch(0.96 0.04 ${hue})`,
                                    color: `oklch(0.36 0.14 ${hue})`,
                                    border: `1px solid ${active ? `oklch(0.50 0.16 ${hue})` : `oklch(0.82 0.10 ${hue})`}`,
                                    boxShadow: active ? `0 0 0 2px oklch(0.85 0.12 ${hue} / 0.4)` : 'none',
                                    borderRadius: 3, cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                  }}
                                >{label}</button>
                              </td>
                            );
                          }
                          const isEventId = c === 'event_id';
                          const cellValue = row[c];
                          const fullValue = formatCell(cellValue);
                          const truncated = isCellTruncated(cellValue);
                          const isPivotCount = groupByField !== null && c === 'count';
                          const isPivotValue = groupByField !== null && c === groupByField;
                          // Pivot mode: count cell renders a horizontal bar
                          // sized to the row's % of total (max in visible
                          // page). Reads as a histogram with zero
                          // additional UI weight.
                          let pivotBarWidth = 0;
                          if (isPivotCount && previewRows && previewRows.length > 0) {
                            const max = Math.max(...previewRows.map(r => Number(r.count) || 0));
                            const cnt = Number(cellValue) || 0;
                            pivotBarWidth = max > 0 ? (cnt / max) * 100 : 0;
                          }
                          const tooltip = isPivotValue
                            ? `Click to drill into ${groupByField}="${fullValue}"`
                            : isPivotCount
                              ? `${fullValue} events`
                              : isEventId
                                ? `Click to open lineage drawer for ${fullValue}`
                                : NON_FILTERABLE_CELLS.has(c)
                                  ? truncated ? 'Hover for full value' : fullValue
                                  : `Click to filter where ${c} = ${fullValue}`;
                          const formatted = formatCell(cellValue, c);
                          const colTy = columnType(c);
                          const numeric = colTy === 'num' || colTy === 'int' || isPivotCount;
                          return (
                            <td
                              key={c}
                              onClick={() => handleCellClick(c, cellValue)}
                              onMouseEnter={truncated ? (e) => scheduleHoverPreview(i, c, e.currentTarget) : undefined}
                              onMouseLeave={truncated ? cancelHoverPreview : undefined}
                              title={tooltip}
                              style={{
                                position: isPivotCount ? 'relative' : 'static',
                                padding: '6px 10px',
                                color: isEventId || isPivotValue ? 'var(--accent)' : 'var(--ink)',
                                maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                cursor: isPivotValue ? 'pointer'
                                  : isPivotCount ? 'default'
                                  : NON_FILTERABLE_CELLS.has(c) && !isEventId ? (truncated ? 'help' : 'default')
                                  : 'pointer',
                                textDecoration: isEventId || isPivotValue ? 'underline dotted' : 'none',
                                textAlign: numeric ? 'right' : 'left',
                                fontVariantNumeric: numeric ? 'tabular-nums' : 'normal',
                              }}
                            >
                              {isPivotCount && (
                                <div style={{
                                  position: 'absolute', left: 0, top: 0, bottom: 0,
                                  width: `${pivotBarWidth}%`,
                                  background: 'oklch(0.92 0.10 145 / 0.5)',
                                  pointerEvents: 'none',
                                }} />
                              )}
                              <span style={{ position: 'relative' }}>
                                {(() => {
                                  // input_tags has a dedicated chip renderer.
                                  if (c === 'input_tags' && cellValue) return renderInputTags(cellValue);
                                  // Generic nested-object detection (B2,
                                  // 2026-05-12). Any cell whose value
                                  // decodes to a JSON object / array
                                  // renders via NestedCell so operators
                                  // see the shape inline with click-to-
                                  // expand. Scalar / null / plain string
                                  // cells fall back to the original
                                  // formatCell+highlight path. Skip on
                                  // envelope columns where we never expect
                                  // nested content.
                                  if (!ENVELOPE_COLUMNS.has(c)) {
                                    const parsed = tryParseJsonObject(cellValue);
                                    if (parsed !== undefined) {
                                      return (
                                        <NestedCell
                                          value={parsed}
                                          compact
                                          pathPrefix={c}
                                          onPinPath={togglePinnedPath}
                                          pinnedPaths={pinnedSet}
                                        />
                                      );
                                    }
                                  }
                                  return searchText.trim() ? renderHighlight(formatted, searchText.trim()) : formatted;
                                })()}
                                {isEventId && eventId && hoverRowId === eventId && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); void replayOne(eventId); }}
                                    disabled={replayingId === eventId}
                                    title={`Replay ${eventId} — re-fires triggers on this single event (idempotent — same event_key)`}
                                    aria-label="Replay this event"
                                    style={{
                                      marginLeft: 6,
                                      padding: '0 6px',
                                      fontSize: 11, fontFamily: 'var(--mono)', lineHeight: 1,
                                      background: 'var(--bg-elev)', border: '1px solid var(--accent-line)',
                                      color: 'var(--accent)', borderRadius: 3, cursor: 'pointer',
                                    }}
                                  >{replayingId === eventId ? '…' : '↻'}</button>
                                )}
                              </span>
                            </td>
                          );
                        })}
                        {/* B3 drill-in cells — operator-pinned nested
                            paths (current.title, meta.action, etc.).
                            Value walked from raw payload via
                            readPinnedValue; renders via NestedCell so
                            even a pinned object path keeps the
                            chip+expand affordance. */}
                        {pinnedPaths.map(p => {
                          const val = readPinnedValue(row, p);
                          return (
                            <td
                              key={`pin:${p}`}
                              style={{
                                padding: '6px 10px',
                                background: 'oklch(0.98 0.02 235 / 0.3)',
                                verticalAlign: 'top',
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <NestedCell
                                value={val}
                                compact
                                pathPrefix={p}
                                onPinPath={togglePinnedPath}
                                pinnedPaths={pinnedSet}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {previewRows && previewRows.length === 0 && !previewError && (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--ink-3)' }}>
              {groupByField && (
                <div>
                  No events have <code style={{ fontFamily: 'var(--mono)' }}>{groupByField}</code> set.
                  <button
                    type="button"
                    onClick={() => setGroupByField(null)}
                    className="btn sm ghost"
                    style={{ padding: '3px 10px', fontSize: 11, marginLeft: 8 }}
                  >Exit pivot</button>
                </div>
              )}
              {!groupByField && !hasRecipeEdits && <div>No rows yet. Send an event to the source — once it arrives the table refreshes automatically.</div>}
              {hasRecipeEdits && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    No rows match this recipe. Try removing one of the filters below or clearing the whole recipe.
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 8 }}>
                    {filters.filter(f => f.field_path.trim()).map((f, idx) => {
                      const opSym = OP_SYMBOL[f.op] ?? f.op;
                      const valStr = !['is_present', 'is_empty'].includes(f.op) && f.value ? ` ${f.value.slice(0, 24)}${f.value.length > 24 ? '…' : ''}` : '';
                      const realIdx = filters.indexOf(f);
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => removeFilter(realIdx)}
                          className="btn sm ghost"
                          style={{ padding: '3px 10px', fontSize: 11.5, fontFamily: 'var(--mono)' }}
                          title="Remove this filter"
                        >× <code>{f.field_path}</code> {opSym}{valStr}</button>
                      );
                    })}
                    {uniqueByPath.trim() && (
                      <button
                        type="button"
                        onClick={() => setUniqueByPath('')}
                        className="btn sm ghost"
                        style={{ padding: '3px 10px', fontSize: 11.5, fontFamily: 'var(--mono)' }}
                        title="Drop the unique-by clause"
                      >× unique by <code>{uniqueByPath.trim()}</code></button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={clearRecipe}
                    className="btn sm ghost"
                    style={{ padding: '3px 12px', fontSize: 11.5 }}
                  >Clear entire recipe</button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Simple Save dialog — the impatient-operator default path.
          One question (name, pre-filled), one Save button. Helper line
          summarises what the view captures so they don't have to
          mentally model it. Advanced link opens the full panel for
          stream-action wiring (forward / dedupe / enrich). */}
      {showSaveSimple && (
        <div
          onClick={() => setShowSaveSimple(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'oklch(0.18 0.01 250 / 0.40)',
            zIndex: 1500,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 8,
              padding: '20px 24px', minWidth: 380, maxWidth: 480,
              boxShadow: '0 12px 40px oklch(0.18 0.01 250 / 0.30)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
              <strong style={{ fontSize: 16 }}>Save as a view</strong>
              <button
                type="button"
                onClick={() => setShowSaveSimple(false)}
                style={{ background: 'transparent', border: 'none', color: 'var(--ink-3)', cursor: 'pointer', fontSize: 16, padding: 0, lineHeight: 1 }}
                aria-label="Close"
              >×</button>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 14 }}>
              {(() => {
                const parts: string[] = [];
                const validF = filters.filter(f => f.field_path.trim()).length;
                if (validF > 0) parts.push(`${validF} filter${validF === 1 ? '' : 's'}`);
                if (uniqueByPath.trim()) parts.push(`unique by \`${uniqueByPath.trim()}\``);
                if (searchText.trim()) parts.push(`search “${searchText.trim().slice(0, 24)}”`);
                if (dateScope !== 'all') parts.push(dateScope === 'hour' ? 'last hour' : dateScope === 'today' ? 'today' : 'last 7 days');
                if (sortColumn) parts.push(`sorted by ${sortColumn} ${sortDir}`);
                if (hiddenColumns.size > 0) parts.push(`${hiddenColumns.size} field${hiddenColumns.size === 1 ? '' : 's'} hidden`);
                if (parts.length === 0) return 'Captures the current table as a tab. You can add filters, search, or hide columns first to make it more specific.';
                return `Captures: ${parts.join(' · ')}.`;
              })()}
            </div>
            <input
              autoFocus
              value={saveName}
              onChange={e => setSaveName(e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'))}
              onKeyDown={e => {
                if (e.key === 'Enter' && !submitting) { void handleQuickSave(saveName); }
                else if (e.key === 'Escape') { setShowSaveSimple(false); }
              }}
              placeholder="recent_orders · letters/digits/underscore"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13,
                border: '1px solid var(--line)', borderRadius: 4,
                background: 'var(--bg)', color: 'var(--ink)',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
              <button
                type="button"
                onClick={() => {
                  setShowSaveSimple(false);
                  setShowSavePanel(true);
                }}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--ink-3)', fontSize: 11.5, padding: 0, textDecoration: 'underline',
                }}
                title="Open the full panel: forward to webhook, dedupe at ingest, enrich, etc."
              >Advanced (stream actions)…</button>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                onClick={() => setShowSaveSimple(false)}
                className="btn sm ghost"
                disabled={submitting}
              >Cancel</button>
              <button
                type="button"
                onClick={() => { void handleQuickSave(saveName); }}
                className="btn sm primary"
                disabled={submitting || !saveName.trim()}
              >{submitting ? 'Saving…' : 'Save view'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Save panel */}
      {showSavePanel && (
        <div style={{ padding: '12px 14px', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 6, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 12.5, color: 'var(--ink)', fontWeight: 500 }}>Save current recipe as a view</div>
          <input
            value={saveName}
            onChange={e => setSaveName(e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'))}
            placeholder="recent_orders · letters/digits/underscore"
            style={{ ...inputStyle, flex: 1 }}
            autoFocus
          />
          <div>
            <div style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginBottom: 4 }}>Also apply at ingest</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {([
                { id: 'none', label: 'View only' },
                { id: 'validate', label: 'Validate gate' },
                { id: 'dedupe', label: 'Dedupe gate' },
                { id: 'forward', label: 'Forward (POST)' },
                { id: 'enrich', label: 'Enrich' },
              ] as const).map(opt => {
                const active = streamAction === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setStreamAction(opt.id)}
                    style={{
                      padding: '4px 10px', fontSize: 11.5,
                      background: active ? 'var(--accent)' : 'var(--bg)',
                      color: active ? 'white' : 'var(--ink-2)',
                      border: `1px solid ${active ? 'var(--accent)' : 'var(--line)'}`,
                      borderRadius: 14, cursor: 'pointer',
                      fontWeight: active ? 500 : 400,
                    }}
                  >{opt.label}</button>
                );
              })}
            </div>
            {streamAction === 'forward' && (
              <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                <input value={forwardUrl} onChange={e => setForwardUrl(e.target.value)} placeholder="https://example.com/webhook" style={{ ...inputStyle, flex: 1 }} />
                <select value={forwardMethod} onChange={e => setForwardMethod(e.target.value as 'POST' | 'PUT')} style={inputStyle}>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                </select>
              </div>
            )}
            {streamAction === 'enrich' && (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <input value={enrichUrl} onChange={e => setEnrichUrl(e.target.value)} placeholder="https://api.example.com/users/{id}" style={inputStyle} />
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={enrichIdPath} onChange={e => setEnrichIdPath(e.target.value)} placeholder="field with the lookup key (e.g. customer_id)" style={{ ...inputStyle, flex: 1 }} />
                  <input value={enrichMergePath} onChange={e => setEnrichMergePath(e.target.value)} placeholder="where to attach the result (e.g. enrichment)" style={{ ...inputStyle, flex: 1 }} />
                </div>
              </div>
            )}
            {streamAction === 'dedupe' && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)', minWidth: 86 }}>Window (sec)</span>
                <input type="number" min={1} max={604800} value={dedupeWindowSec} onChange={e => setDedupeWindowSec(Number(e.target.value) || 3600)} style={inputStyle} />
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn sm ghost" onClick={() => setShowSavePanel(false)} disabled={submitting}>Cancel</button>
            <button className="btn sm primary" onClick={() => { void handleSave(); }} disabled={submitting || !saveName.trim()}>
              {submitting ? 'Saving…' : streamAction === 'none' ? 'Save view' : `Save view + ${streamAction} trigger`}
            </button>
          </div>
        </div>
      )}

      {/* Select Fields drawer — slides in from the right with the full
          field list, type/origin metadata, occurrence%, and a sample
          value per row. Filter input at the top with a property-mode
          dropdown matches BigQuery's "Select Fields" affordance. Save
          commits the visible columns; Cancel discards. */}
      {showFieldsDrawer && (
        <div
          onClick={cancelFieldsDrawer}
          onKeyDown={e => { if (e.key === 'Escape') cancelFieldsDrawer(); }}
          style={{
            position: 'fixed', inset: 0,
            background: 'oklch(0.18 0.01 250 / 0.35)',
            display: 'flex', justifyContent: 'flex-end',
            zIndex: 1400,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: 'min(560px, 100vw)', height: '100vh',
              background: 'var(--bg)', borderLeft: '1px solid var(--line)',
              display: 'flex', flexDirection: 'column',
              boxShadow: '-8px 0 32px oklch(0.18 0.01 250 / 0.18)',
            }}
          >
            <div style={{
              padding: '20px 24px 14px', borderBottom: '1px solid var(--line)',
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            }}>
              <strong style={{ fontSize: 16 }}>Select Fields</strong>
              <button
                type="button"
                onClick={cancelFieldsDrawer}
                aria-label="Close"
                style={{ background: 'transparent', border: 'none', color: 'var(--ink-3)', cursor: 'pointer', fontSize: 18, padding: 0, lineHeight: 1 }}
              >×</button>
            </div>
            {(() => {
              const total = allTableColumns.length;
              const selected = allTableColumns.filter(c => !draftHidden.has(c)).length;
              return (
                <div style={{
                  margin: '12px 24px 0',
                  padding: '8px 12px',
                  background: selected === 0 ? 'oklch(0.96 0.06 75)' : 'var(--accent-soft)',
                  border: `1px solid ${selected === 0 ? 'oklch(0.78 0.10 75)' : 'var(--accent-line)'}`,
                  borderRadius: 6,
                  fontSize: 12,
                  color: selected === 0 ? 'oklch(0.36 0.14 75)' : 'var(--accent)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ fontSize: 14 }}>{selected === 0 ? '⚠' : 'ℹ'}</span>
                  <span>
                    {selected === 0
                      ? 'No fields selected — table will hide every column on Save.'
                      : `${selected} of ${total} field${total === 1 ? '' : 's'} will be visible.`}
                  </span>
                </div>
              );
            })()}
            <div style={{
              padding: '12px 24px 8px',
              display: 'flex', gap: 8, alignItems: 'center',
            }}>
              <span style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Filter</span>
              <input
                value={fieldFilter}
                onChange={e => setFieldFilter(e.target.value)}
                placeholder="Enter property name or value"
                autoFocus
                style={{
                  flex: 1, padding: '6px 10px', fontFamily: 'var(--ui)', fontSize: 13,
                  border: '1px solid var(--line)', borderRadius: 4,
                  background: 'var(--bg-elev)', color: 'var(--ink)',
                }}
              />
              <select
                value={fieldFilterMode}
                onChange={e => setFieldFilterMode(e.target.value as typeof fieldFilterMode)}
                style={{
                  padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 12,
                  border: '1px solid var(--line)', borderRadius: 4,
                  background: 'var(--bg-elev)', color: 'var(--ink)',
                }}
                title="Restrict the filter to a property dimension"
              >
                <option value="any">any</option>
                <option value="name">name</option>
                <option value="type">type</option>
                <option value="origin">origin</option>
              </select>
            </div>
            <div style={{
              padding: '0 24px 12px',
              display: 'flex', gap: 6, flexWrap: 'wrap',
            }}>
              <button
                type="button"
                onClick={() => setDraftHidden(new Set())}
                className="btn sm ghost"
                style={{ padding: '3px 10px', fontSize: 11 }}
              >Select all</button>
              <button
                type="button"
                onClick={() => setDraftHidden(new Set(allTableColumns))}
                className="btn sm ghost"
                style={{ padding: '3px 10px', fontSize: 11 }}
              >Select none</button>
              <button
                type="button"
                onClick={() => setDraftHidden(new Set(allTableColumns.filter(c => c !== 'event_id' && c !== 'received_at')))}
                className="btn sm ghost"
                style={{ padding: '3px 10px', fontSize: 11 }}
                title="Keep only event_id + received_at"
              >Minimal</button>
              <button
                type="button"
                onClick={() => setDraftHidden(new Set(allTableColumns.filter(c => fieldOrigin(c) !== 'payload')))}
                className="btn sm ghost"
                style={{ padding: '3px 10px', fontSize: 11 }}
                title="Hide everything except payload columns"
              >Payload only</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 24px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 1 }}>
                  <tr>
                    <th style={fieldsThStyle}></th>
                    <th style={fieldsThStyle}>Field name</th>
                    <th style={fieldsThStyle}>Type</th>
                    <th style={fieldsThStyle}>Origin</th>
                    <th style={{ ...fieldsThStyle, textAlign: 'right' }}>Occ.</th>
                    <th style={fieldsThStyle}>Sample</th>
                  </tr>
                </thead>
                <tbody>
                  {allTableColumns.map(c => {
                    const ty = columnType(c);
                    const origin = fieldOrigin(c);
                    if (!matchFieldFilter(c, ty, origin, fieldFilter, fieldFilterMode)) return null;
                    const visible = !draftHidden.has(c);
                    const schemaField = (schema ?? []).find(f => f.path === c);
                    const occPct = schemaField && previewRows && previewRows.length > 0
                      ? Math.round((schemaField.occurrences / Math.max(1, ...((schema ?? []).map(f => f.occurrences)))) * 100)
                      : null;
                    const sample = schemaField && schemaField.sample_values.length > 0
                      ? formatSampleValue(schemaField.sample_values[0])
                      : '';
                    return (
                      <tr key={c} style={{ borderBottom: '1px solid var(--line)' }}>
                        <td style={{ padding: '6px 8px', width: 28 }}>
                          <input
                            type="checkbox"
                            checked={visible}
                            onChange={() => setDraftHidden(prev => {
                              const next = new Set(prev);
                              if (next.has(c)) next.delete(c); else next.add(c);
                              return next;
                            })}
                            style={{ margin: 0, cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: '6px 8px', fontFamily: 'var(--mono)', color: visible ? 'var(--ink)' : 'var(--ink-3)' }}>{c}</td>
                        <td style={{ padding: '6px 8px' }}>
                          {ty && (
                            <span style={fieldTypeChipStyle}>{ty}</span>
                          )}
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <span style={{
                            ...fieldOriginChipStyle,
                            background: origin === 'payload' ? 'var(--accent-soft)'
                              : origin === 'system' ? 'oklch(0.96 0.04 280)'
                              : 'var(--bg-sunken)',
                            color: origin === 'payload' ? 'var(--accent)'
                              : origin === 'system' ? 'oklch(0.36 0.10 280)'
                              : 'var(--ink-3)',
                            border: `1px solid ${origin === 'payload' ? 'var(--accent-line)' : 'var(--line)'}`,
                          }}>{origin}</span>
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>
                          {occPct !== null ? `${occPct}%` : '—'}
                        </td>
                        <td style={{
                          padding: '6px 8px', fontFamily: 'var(--mono)', fontSize: 11,
                          color: 'var(--ink-3)',
                          maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }} title={sample}>{sample || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{
              padding: '12px 24px',
              borderTop: '1px solid var(--line)',
              background: 'var(--bg-elev)',
              display: 'flex', gap: 8, justifyContent: 'flex-end',
            }}>
              <button type="button" onClick={cancelFieldsDrawer} className="btn sm ghost">Cancel</button>
              <button type="button" onClick={commitFieldsDrawer} className="btn sm primary">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Field-value distribution drawer — extracted to its own
          component. Side panel with top-50 unique values + counts +
          relative share bars + click-to-filter. */}
      <DistributionDrawer
        field={distributionField}
        loading={distributionLoading}
        rows={distributionRows}
        durationMs={distributionDurationMs}
        setField={setDistributionField}
        setGroupByField={setGroupByField}
        setFilters={setFilters}
        toast={toast}
      />

      {/* Keyboard shortcuts cheat sheet — extracted to its own
          component for agent-velocity. Toggled by `?` or the toolbar
          button; backdrop click closes via onClose. */}
      <ShortcutsCheatSheet open={showShortcuts} onClose={() => setShowShortcuts(false)} />

      {/* Column-header context menu — extracted to its own component.
          Right-click on any data-table header to sort / hide / filter /
          show-distribution / group-by on that column. */}
      <ColumnHeaderMenu
        state={headerMenu}
        sortColumn={sortColumn}
        setSortColumn={setSortColumn}
        setSortDir={setSortDir}
        setDistributionField={setDistributionField}
        setGroupByField={setGroupByField}
        toggleColumnHidden={toggleColumnHidden}
        setFilters={setFilters}
        setHeaderMenu={setHeaderMenu}
        toast={toast}
      />

      {/* Inline cell preview popover — extracted to its own component. */}
      <CellHoverPreview state={hoverPreview} rows={previewRows} />

      {/* Lineage drawer — slides in when an event_id cell is clicked.
          Shows parent chain, derivative children, and which triggers
          fired on the event. Reused from V2. */}
      {lineageEventId && (
        <LineageDrawer
          sourceId={sourceId}
          eventId={lineageEventId}
          triggers={triggers}
          onClose={() => setLineageEventId(null)}
        />
      )}
    </div>
  );
}

/** ViewTab — single tab in the views row. Operator paths beyond
 *  plain activation:
 *    1. Hover → ⋯ menu (Rename · Duplicate · Delete · Open in BQ · Copy SQL)
 *    2. Double-click name → inline rename
 *  The 'events' base view is the auto-created envelope view; we
 *  hide its destructive actions since it's not deletable.
 *  `dataset` is the per-source BQ dataset used to build the
 *  Open-in-BigQuery deep-link. */
